<template>
  <div class="user-center">
    <div class="container">
      <div class="main-content">
        <!-- 左侧菜单 -->
        <aside class="side-menu">
          <!-- 实名认证 / 青少年模式：醒目入口卡（置于菜单最顶部） -->
          <div class="side-banner" :class="{ active: currentTab === 'realname' }" @click="switchTab('realname')">
            <svg class="sb-icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/>
            </svg>
            <div class="sb-text">
              <div class="sb-title">实名认证 · 青少年模式</div>
              <div class="sb-sub">{{ sideBannerSub }}</div>
            </div>
            <span class="sb-arrow" aria-hidden="true">›</span>
          </div>
          <div class="menu-list">
            <div v-for="group in menuGroups" :key="group.key" class="menu-group">
              <div class="menu-group-title">{{ group.title }}</div>
              <div
                v-for="item in group.items"
                :key="item.key"
                class="menu-item"
                :class="{ active: currentTab === item.key, soon: item.soon }"
                @click="switchTab(item.key)"
              >
                <span class="menu-icon" v-html="item.icon"></span>
                <span class="menu-text">{{ item.text }}</span>
                <span v-if="item.badge" class="menu-badge"></span>
              </div>
            </div>
          </div>
        </aside>

        <!-- 右侧内容 -->
        <main class="content-area">
          <!-- 内容面板 -->
          <div class="panel">
            <div class="panel-header">
              <h3>{{ currentMenu.text }}</h3>
              <p v-if="currentMenu.subtitle">{{ currentMenu.subtitle }}</p>
            </div>

            <div v-if="loading" class="panel-loading">加载中...</div>

            <template v-else>
              <!-- 个人中心 -->
              <div v-if="currentTab === 'home'" class="panel-body">
                <div class="home-dashboard">
                  <div class="home-welcome">
                    <img :src="avatarUrl" class="home-avatar" @error="handleAvatarError" alt="">
                    <div class="home-info">
                      <div class="home-name">{{ userInfo.nickname || '未设置昵称' }}</div>
                      <div class="home-level">
                        <span class="level-badge">Lv {{ liveLevel }}</span>
                        <span class="level-tag" v-if="!minorMode" @click="switchTab('level')">加速升级</span>
                      </div>
                    </div>
                  </div>
                  <div class="home-actions">
                    <button class="home-action" @click="switchTab('assets')">
                      <span class="home-action-icon asset-action-icon">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="16" cy="12" r="1"/></svg>
                      </span>
                      <span><b>我的财产</b><small>查看余额与金币</small></span>
                    </button>
                    <button class="home-action" v-if="!minorMode" @click="switchTab('recharge')">
                      <span class="home-action-icon recharge-action-icon">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg>
                      </span>
                      <span><b>充值中心</b><small>购买余额套餐</small></span>
                    </button>
                    <button class="home-action" v-if="!minorMode" @click="switchTab('commission')">
                      <span class="home-action-icon commission-action-icon">
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                      </span>
                      <span><b>我的佣金</b><small>主播收益与提现</small></span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- 我的信息 -->
              <div v-if="currentTab === 'basic'" class="panel-body">
                <div class="basic-page">
                  <!-- 顶部用户卡 -->
                  <div class="basic-header">
                    <div class="basic-user">
                      <div class="basic-avatar-wrap">
                        <img :src="avatarUrl" class="basic-avatar" @error="handleAvatarError" alt="">
                        <div class="basic-avatar-edit">
                          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M20 4h-3.17L15 2H9L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                          <input type="file" accept="image/*" @change="handleAvatarChange">
                        </div>
                      </div>
                      <div class="basic-meta">
                        <div class="basic-name">{{ userInfo.nickname || '未设置昵称' }}</div>
                        <div class="basic-id">灵眸号：{{ userInfo.id || '-' }}</div>
                        <div class="basic-level">
                          <span class="level-badge">Lv {{ liveLevel }}</span>
                          <span class="basic-level-tag" v-if="!minorMode" @click="switchTab('level')">升级</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 子标签 -->
                  <div class="basic-tabs">
                    <span class="basic-tab" :class="{ active: basicSubTab === 'profile' }" @click="basicSubTab = 'profile'">基础信息</span>
                    <span class="basic-tab" :class="{ active: basicSubTab === 'account' }" @click="basicSubTab = 'account'">账号设置</span>
                    <span class="basic-tab" v-if="!minorMode" :class="{ active: basicSubTab === 'more' }" @click="basicSubTab = 'more'">更多玩法</span>
                  </div>

                  <!-- 基础信息 -->
                  <div v-if="basicSubTab === 'profile'" class="basic-section">
                    <div class="basic-section-title">基础信息</div>
                    <div class="info-form-grid">
                      <div class="form-row">
                        <label>昵称</label>
                        <input type="text" v-model="editForm.nickname" placeholder="请输入昵称" maxlength="20">
                      </div>
                      <div class="form-row">
                        <label>用户名</label>
                        <input type="text" v-model="editForm.username" placeholder="请输入用户名" maxlength="30">
                      </div>
                      <div class="form-row">
                        <label>性别</label>
                        <div class="gender-choices">
                          <span :class="{ active: editForm.gender === 1 }" @click="editForm.gender = 1">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#3b82f6" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 10h8"/></svg> 男
                          </span>
                          <span :class="{ active: editForm.gender === 2 }" @click="editForm.gender = 2">
                            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#ec4899" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 12h8"/></svg> 女
                          </span>
                          <span :class="{ active: editForm.gender === 0 }" @click="editForm.gender = 0">保密</span>
                        </div>
                      </div>
                      <div class="form-row">
                        <label>个性签名</label>
                        <textarea v-model="editForm.bio" placeholder="点击编辑个性签名" rows="3" maxlength="100"></textarea>
                      </div>
                      <div class="form-row">
                        <label>注册时间</label>
                        <div class="form-static">{{ userInfo.createtime || '-' }}</div>
                      </div>
                    </div>
                    <div class="form-actions">
                      <button class="btn-primary" @click="saveUserInfo">保存修改</button>
                    </div>
                  </div>

                  <!-- 账号设置 -->
                  <div v-if="basicSubTab === 'account'" class="basic-section">
                    <div class="basic-section-title">账号安全</div>
                    <div class="security-list">
                      <div class="security-item">
                        <div class="security-icon blue">
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
                        </div>
                        <div class="security-info">
                          <div class="security-name">手机绑定</div>
                          <div class="security-desc">{{ userInfo.mobile ? userInfo.mobile.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2') : '未绑定手机号' }}</div>
                        </div>
                        <button class="security-btn" @click="openBindModal()">{{ userInfo.mobile ? '更换' : '绑定' }}</button>
                      </div>
                    </div>

                    <div class="basic-section-title" style="margin-top: 28px;">修改密码</div>
                    <div class="info-form-grid">
                      <div class="form-row">
                        <label>当前密码</label>
                        <input type="password" v-model="accountForm.oldPwd" placeholder="请输入当前密码">
                      </div>
                      <div class="form-row">
                        <label>新密码</label>
                        <input type="password" v-model="accountForm.newPwd" placeholder="请输入新密码（6-30位）">
                      </div>
                      <div class="form-row">
                        <label>确认新密码</label>
                        <input type="password" v-model="accountForm.confirmPwd" placeholder="请再次输入新密码">
                      </div>
                    </div>
                    <div class="form-actions">
                      <button class="btn-primary" @click="savePassword">确认修改</button>
                    </div>
                  </div>

                  <!-- 更多玩法：青少年模式下四项全部受限，整块隐藏避免空标题 -->
                  <div v-if="basicSubTab === 'more' && !minorMode" class="basic-section">
                    <div class="basic-section-title">更多玩法</div>
                    <div class="more-grid">
                      <div class="more-item" @click="switchTab('anchor')">
                        <div class="more-icon orange">
                          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
                        </div>
                        <div class="more-name">主播招募</div>
                        <div class="more-desc">成为主播，开启直播之旅</div>
                      </div>
                      <div class="more-item" @click="switchTab('creator')">
                        <div class="more-icon blue">
                          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </div>
                        <div class="more-name">创作中心</div>
                        <div class="more-desc">发布作品，查看数据</div>
                      </div>
                      <div class="more-item" @click="switchTab('task')">
                        <div class="more-icon green">
                          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M12 8l1.5 3.5H17l-3 2.5 1 3.5-3-2.5-3 2.5 1-3.5-3-2.5h3.5L12 8z"/></svg>
                        </div>
                        <div class="more-name">任务中心</div>
                        <div class="more-desc">完成任务，赚取奖励</div>
                      </div>
                      <div class="more-item" v-if="!minorMode" @click="switchTab('cdkey')">
                        <div class="more-icon purple">
                          <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="10" rx="2"/><path d="M7 7v10M17 7v10"/></svg>
                        </div>
                        <div class="more-name">CDKey兑换</div>
                        <div class="more-desc">兑换礼包，领取福利</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 我的等级 -->
              <div v-if="currentTab === 'level'" class="panel-body">
                <div class="level-page">
                  <div class="level-header">
                    <img :src="avatarUrl" class="level-avatar" @error="handleAvatarError" alt="">
                    <div class="level-header-info">
                      <div class="lh-name">
                        {{ userInfo.nickname || '未设置昵称' }}
                        <span class="lh-gender" v-html="genderIcon"></span>
                        <span class="lh-edit" @click="switchTab('basic')">
                          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                        </span>
                      </div>
                      <div class="lh-level">
                        <span class="lh-level-badge">
                          <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                          </svg>
                          Lv {{ liveLevel }}
                        </span>
                        <span class="lh-level-tag" @click="switchTab('task')">加速升级</span>
                      </div>
                      <div class="lh-progress-row">
                        <span class="lh-level-text">LV-{{ liveLevel }}</span>
                        <div class="progress-bar"><div class="progress-fill" :style="{ width: levelPercent + '%' }"></div></div>
                        <span class="lh-level-text">LV-{{ liveLevel + 1 }}</span>
                      </div>
                      <div class="lh-progress-sub">{{ liveExp }}/{{ liveNeedExp }}</div>
                    </div>
                  </div>

                  <!-- 经验来源明细 -->
                  <div v-if="expInfo" class="exp-detail-card">
                    <div class="exp-detail-title">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#ff6a00" stroke-width="2">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                      </svg>
                      今日经验来源
                      <span class="exp-detail-total">今日获得 {{ expInfo.today_total || expInfo.total || 0 }} 经验</span>
                    </div>
                    <div class="exp-detail-list">
                      <div class="exp-detail-item">
                        <div class="exp-detail-icon watch">
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="2" y="6" width="18" height="12" rx="3"/><circle cx="11" cy="12" r="3" fill="currentColor"/><path d="M22 9v6"/>
                          </svg>
                        </div>
                        <div class="exp-detail-info">
                          <div class="exp-detail-name">观看直播</div>
                          <div class="exp-detail-desc">观看主播直播获得经验</div>
                        </div>
                        <div class="exp-detail-value">+{{ expInfo.watch_exp || expInfo.watch || 0 }}</div>
                      </div>
                      <div class="exp-detail-item">
                        <div class="exp-detail-icon gift">
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                          </svg>
                        </div>
                        <div class="exp-detail-info">
                          <div class="exp-detail-name">送礼互动</div>
                          <div class="exp-detail-desc">给主播送礼物获得经验</div>
                        </div>
                        <div class="exp-detail-value">+{{ expInfo.gift_exp || expInfo.gift || 0 }}</div>
                      </div>
                      <div class="exp-detail-item">
                        <div class="exp-detail-icon interact">
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                          </svg>
                        </div>
                        <div class="exp-detail-info">
                          <div class="exp-detail-name">互动行为</div>
                          <div class="exp-detail-desc">发弹幕、点赞等互动获得经验</div>
                        </div>
                        <div class="exp-detail-value">+{{ expInfo.interact_exp || expInfo.interact || 0 }}</div>
                      </div>
                      <div class="exp-detail-item">
                        <div class="exp-detail-icon task">
                          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="3" y="3" width="18" height="18" rx="3"/><path d="M8 12h8M8 8h8M8 16h5"/>
                          </svg>
                        </div>
                        <div class="exp-detail-info">
                          <div class="exp-detail-name">任务奖励</div>
                          <div class="exp-detail-desc">签到、完成日常/新手任务获得经验</div>
                        </div>
                        <div class="exp-detail-value">+{{ expInfo.task_exp || 0 }}</div>
                      </div>
                    </div>
                    <div v-if="expInfo.tip" class="exp-detail-tip">{{ expInfo.tip }}</div>
                  </div>

                  <div class="level-section-title">
                    <span>等级特权</span>
                    <span class="level-all" @click="showAllPrivileges = true">全部等级特权 <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg></span>
                  </div>
                  <div class="level-privileges">
                    <div class="lp-item" v-for="(p, i) in levelPrivileges" :key="p.id || p.name || i">
                      <div class="lp-icon" :class="p.color" v-html="p.icon"></div>
                      <div class="lp-text">{{ p.text }}</div>
                      <div class="lp-lv">{{ p.lv }}</div>
                    </div>
                  </div>

                  <!-- 全部等级特权弹窗 -->
                  <div v-if="showAllPrivileges" class="modal-overlay" @click="showAllPrivileges = false">
                    <div class="modal-content privileges-modal" @click.stop>
                      <h3>全部等级特权</h3>
                      <div class="privilege-level-list">
                        <div class="pl-item" v-for="(p, i) in allPrivileges" :key="p.id || p.name || i">
                          <div class="pl-lv">{{ p.lv }}</div>
                          <div class="pl-text">{{ p.text }}</div>
                        </div>
                      </div>
                      <button class="btn-primary" @click="showAllPrivileges = false">知道了</button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 关注与历史 -->
              <div v-if="currentTab === 'follow_history'" class="panel-body">
                <div class="sub-tabs">
                  <span class="sub-tab" :class="{ active: subTab === 'follow' }" @click="subTab = 'follow'">关注</span>
                  <span class="sub-tab" :class="{ active: subTab === 'history' }" @click="subTab = 'history'">观看记录</span>
                </div>
                <FollowList v-if="subTab === 'follow'" :embedded="true" />
                <WatchHistory v-if="subTab === 'history'" :embedded="true" />
              </div>

              <!-- 我的动态 -->
              <div v-if="currentTab === 'posts'" class="panel-body no-padding">
                <PostFeed :embedded="true" />
              </div>

              <!-- 我的消息 -->
              <div v-if="currentTab === 'messages'" class="panel-body">
                <div class="message-layout">
                  <!-- 顶部标签栏 -->
                  <div class="message-tabs">
                    <span class="message-tab" :class="{ active: messageTab === 'system' }" @click="switchMessageTab('system')">
                      系统通知
                      <i class="msg-badge" v-if="messageUnread.system > 0">{{ messageUnread.system }}</i>
                    </span>
                    <span class="message-tab" :class="{ active: messageTab === 'interact' }" @click="switchMessageTab('interact')">
                      互动通知
                      <i class="msg-badge" v-if="messageUnread.interact > 0">{{ messageUnread.interact }}</i>
                    </span>
                    <span class="message-tab" :class="{ active: messageTab === 'private' }" @click="switchMessageTab('private')">
                      私信
                      <i class="msg-badge" v-if="messageUnread.private > 0">{{ messageUnread.private }}</i>
                    </span>
                    <span class="message-readall" v-if="messageTab !== 'private'" @click="markAllRead">全部已读</span>
                  </div>

                  <!-- 系统通知 / 互动通知 -->
                  <div v-if="messageTab !== 'private'" class="message-notice">
                    <div v-if="messageList.length === 0" class="empty-text">暂无消息</div>
                    <div v-else class="msg-notice-list">
                      <div class="msg-notice-item" :class="{ unread: !message.is_read }" v-for="message in messageList" :key="message.id" @click="markRead(message.id)">
                        <div class="msg-notice-title">
                          {{ message.title }}
                          <i class="msg-dot" v-if="!message.is_read"></i>
                        </div>
                        <div class="msg-notice-content">{{ message.content }}</div>
                        <div class="msg-notice-time">{{ message.createtime }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- 私信 -->
                  <div v-else class="message-body">
                    <div class="message-list">
                      <div v-if="conversationList.length === 0" class="empty-text">暂无私信</div>
                      <div v-else>
                        <div class="msg-item" :class="{ active: currentConversation && currentConversation.id === conv.user_id }" v-for="conv in conversationList" :key="conv.user_id" @click="openConversation(conv.user_id)">
                          <img :src="convAvatar(conv)" class="msg-avatar-img" @error="handleAvatarError" alt="">
                          <div class="msg-info">
                            <div class="msg-name">{{ conv.nickname }}</div>
                            <div class="msg-preview">{{ conv.last_message || conv.content || '' }}</div>
                          </div>
                          <i class="msg-badge" v-if="conv.unread > 0">{{ conv.unread }}</i>
                        </div>
                      </div>
                    </div>
                    <div class="message-detail">
                      <div v-if="!currentConversation" class="message-empty">
                        <img :src="defaultAvatarUrl" alt="">
                        <p>你还未选中聊天对象哦~</p>
                      </div>
                      <template v-else>
                        <div class="conv-header">{{ currentConversation.nickname || '对话' }}</div>
                        <div class="conv-messages">
                          <div v-if="conversationMessages.length === 0" class="empty-text">暂无消息记录</div>
                          <div class="conv-msg" :class="{ self: msg.is_self }" v-for="msg in conversationMessages" :key="msg.id">
                            <span>{{ msg.content }}</span>
                          </div>
                        </div>
                        <div class="conv-input">
                          <input type="text" v-model="messageInput" placeholder="输入消息..." @keyup.enter="handleSendMessage">
                          <button class="btn-primary" @click="handleSendMessage">发送</button>
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 我的贵族 -->
              <div v-if="currentTab === 'noble'" class="panel-body no-padding">
                <NobleShop :embedded="true" />
              </div>

              <!-- 任务中心 -->
              <div v-if="currentTab === 'task'" class="panel-body">
                <div class="task-center">
                  <div class="task-section">
                    <h4 class="task-section-title">每日任务</h4>
                    <div class="task-list">
                      <div class="task-item" v-for="(task, i) in dailyTasks" :key="task.id || task.task_key || i">
                        <div class="task-icon" :class="task.color" v-html="task.icon"></div>
                        <div class="task-info">
                          <div class="task-name">{{ task.name }}</div>
                          <div class="task-desc">{{ task.desc }}</div>
                          <div class="task-progress">
                            <div class="task-progress-bar"><div class="task-progress-fill" :style="{ width: Math.min(task.target_count ? (task.done_count / task.target_count) * 100 : 0, 100) + '%' }"></div></div>
                            <span class="task-progress-text">{{ task.done_count }}/{{ task.target_count }}</span>
                          </div>
                        </div>
                        <button v-if="task.claimed" class="task-btn done" disabled>已领取</button>
                        <button v-else-if="task.task_key === 'daily_sign' && task.done_count >= task.target_count" class="task-btn reward" @click="signinDaily">领取奖励</button>
                        <button v-else-if="task.task_key === 'daily_sign'" class="task-btn reward" @click="signinDaily">立即签到</button>
                        <button v-else-if="task.done_count >= task.target_count" class="task-btn reward" @click="claimReward(task)">领取奖励</button>
                        <button v-else class="task-btn" @click="goDoTask(task)">去完成</button>
                      </div>
                    </div>
                  </div>
                  <div class="task-section">
                    <h4 class="task-section-title">新手任务</h4>
                    <div class="task-list">
                      <div class="task-item" v-for="(task, i) in newbieTasks" :key="task.id || task.task_key || i">
                        <div class="task-icon" :class="task.color" v-html="task.icon"></div>
                        <div class="task-info">
                          <div class="task-name">{{ task.name }}</div>
                          <div class="task-desc">{{ task.desc }}</div>
                        </div>
                        <button v-if="task.claimed" class="task-btn done" disabled>已领取</button>
                        <button v-else-if="task.done_count >= task.target_count" class="task-btn reward" @click="claimReward(task)">领取奖励</button>
                        <button v-else class="task-btn" @click="goDoTask(task)">去完成</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 我的头衔 -->
              <div v-if="currentTab === 'title'" class="panel-body no-padding">
                <TitlePanel :embedded="true" />
              </div>

              <!-- 我是房管 -->
              <div v-if="currentTab === 'roomadmin'" class="panel-body no-padding">
                <RoomAdminPanel :embedded="true" />
              </div>

              <!-- 创作中心 -->
              <div v-if="currentTab === 'creator'" class="panel-body">
                <CreatorPanel :embedded="true" />
              </div>

              <!-- 我的佣金 -->
              <div v-if="currentTab === 'commission'" class="panel-body">
                <div class="commission-page">
                  <div class="commission-stats">
                    <div class="comm-stat-card">
                      <div class="comm-stat-value">¥{{ commissionData.total || '0.00' }}</div>
                      <div class="comm-stat-label">总佣金</div>
                    </div>
                    <div class="comm-stat-card">
                      <div class="comm-stat-value">¥{{ commissionData.available || '0.00' }}</div>
                      <div class="comm-stat-label">可提现</div>
                    </div>
                    <div class="comm-stat-card">
                      <div class="comm-stat-value">¥{{ commissionData.pending || '0.00' }}</div>
                      <div class="comm-stat-label">待结算</div>
                    </div>
                    <div class="comm-stat-card">
                      <div class="comm-stat-value">¥{{ commissionData.withdrawn || '0.00' }}</div>
                      <div class="comm-stat-label">已提现</div>
                    </div>
                  </div>
                  <button class="btn-primary" @click="showWithdrawModal = true">申请提现</button>
                  <div class="comm-records">
                    <h4>佣金记录</h4>
                    <div v-if="commissionData.records && commissionData.records.length === 0" class="empty-text">暂无记录</div>
                    <table v-else class="data-table">
                      <thead><tr><th>时间</th><th>来源</th><th>金额</th><th>状态</th></tr></thead>
                      <tbody>
                        <tr v-for="(r, i) in (commissionData.records || [])" :key="r.id || r.order_no || i">
                          <td>{{ r.createtime }}</td><td>{{ r.source }}</td><td>¥{{ r.amount }}</td><td>{{ r.status_text }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- 我的掉宝 -->
              <div v-if="currentTab === 'treasure'" class="panel-body">
                <div class="treasure-page">
                  <!-- 宝箱状态卡 -->
                  <div v-if="treasureStatus" class="treasure-status-card" :class="{ ready: (treasureStatus.available || treasureStatus.count || 0) > 0 }">
                    <div class="treasure-status-icon" :class="{ shake: (treasureStatus.available || treasureStatus.count || 0) > 0 }">
                      <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="1.8">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                        <line x1="12" y1="22.08" x2="12" y2="12"/>
                      </svg>
                    </div>
                    <div class="treasure-status-info">
                      <div class="treasure-status-title">宝箱状态</div>
                      <div class="treasure-status-desc">
                        可开启宝箱：<b class="treasure-status-count">{{ treasureStatus.available || treasureStatus.count || 0 }}</b> / {{ treasureStatus.total_limit || 10 }} 个
                        <span v-if="treasureStatus.next_refresh_time" class="treasure-status-next">（{{ treasureStatus.next_refresh_time }}刷新）</span>
                      </div>
                      <div v-if="treasureStatus.tip" class="treasure-status-tip">{{ treasureStatus.tip }}</div>
                    </div>
                    <button
                      class="treasure-open-btn"
                      :disabled="treasureOpening || (treasureStatus.available || treasureStatus.count || 0) <= 0"
                      @click="handleOpenTreasure"
                    >
                      <svg v-if="treasureOpening" class="loading-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                      </svg>
                      {{ treasureOpening ? '开启中...' : '一键开启' }}
                    </button>
                  </div>

                  <div class="treasure-section-title">掉宝记录</div>
                  <div v-if="treasureList.length === 0" class="empty-text">暂无掉宝记录</div>
                  <div v-else class="treasure-list">
                    <div class="treasure-card" v-for="t in treasureList" :key="t.id">
                      <div class="treasure-icon"><svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor"><path d="M20 6h-2.18A3 3 0 0 0 12 3.18V3a1 1 0 0 0-2 0v.18A3 3 0 0 0 4.18 6H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2z"/></svg></div>
                      <div class="treasure-info">
                        <div class="treasure-name">{{ t.name }}</div>
                        <div class="treasure-reward">{{ t.reward }}</div>
                        <div class="treasure-time">{{ t.createtime }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 我的财产 -->
              <div v-if="currentTab === 'assets'" class="panel-body">
                <div class="assets-overview">
                  <section class="asset-balance-panel">
                    <div class="asset-balance-head">
                      <div>
                        <div class="asset-eyebrow">可用余额</div>
                        <div class="asset-main-value">¥{{ userInfo.money || '0.00' }}</div>
                      </div>
                      <button class="btn-primary" v-if="!minorMode" @click="switchTab('recharge')">立即充值</button>
                    </div>
                    <p class="asset-note" v-if="!minorMode">余额用于礼物、贵族和平台内消费，充值与支付统一在充值中心完成。</p>
                    <p class="asset-note" v-else>青少年模式下已关闭充值与打赏功能，余额仅作展示。</p>
                  </section>
                  <section class="asset-score-panel">
                    <div class="asset-score-icon">
                      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/><path d="M8 12h8M12 8v8"/></svg>
                    </div>
                    <div>
                      <div class="asset-eyebrow">平台金币</div>
                      <div class="asset-score-value">{{ userInfo.score || 0 }}</div>
                    </div>
                    <button class="asset-link" v-if="!minorMode" @click="switchTab('task')">去做任务</button>
                  </section>
                </div>
                <div class="asset-guides">
                  <div v-if="!minorMode"><b>余额</b><span>现金充值所得，用于平台付费消费</span></div>
                  <div><b>金币</b><span>完成任务和参与活动获得，不可提现</span></div>
                  <div v-if="!minorMode"><b>主播收入</b><span>礼物分成单独计入“我的佣金”</span><button @click="switchTab('commission')">查看佣金</button></div>
                </div>
              </div>

              <!-- CDKey 兑换 -->
              <div v-if="currentTab === 'cdkey'" class="panel-body">
                <div class="cdkey-form">
                  <div class="form-row">
                    <label>CDKey 兑换码</label>
                    <input type="text" v-model="cdkey" placeholder="请输入兑换码">
                  </div>
                  <div class="form-actions">
                    <button class="btn-primary" @click="handleCdkey">立即兑换</button>
                  </div>
                </div>
                <div class="cdkey-records" v-if="cdkeyRecords.length > 0">
                  <h4>兑换记录</h4>
                  <div class="cdkey-record-item" v-for="(r, i) in cdkeyRecords" :key="r.id || r.code || i">
                    <span class="cdkey-code">{{ r.code }}</span>
                    <span class="cdkey-reward">{{ r.reward_type === 'score' ? r.reward_amount + '金币' : r.reward_type === 'money' ? '¥' + r.reward_amount : r.reward_type === 'vip_days' ? r.reward_amount + '天VIP' : r.reward_type === 'gift' ? (r.gift_name || '礼物') + ' x' + (r.gift_count || 1) : r.reward_amount }}</span>
                    <span class="cdkey-time">{{ r.createtime }}</span>
                  </div>
                </div>
              </div>

              <!-- 认证/青少年 -->
              <div v-if="currentTab === 'realname'" class="panel-body no-padding">
                <RealnamePanel :embedded="true" />
              </div>

              <!-- 主播招募 -->
              <div v-if="currentTab === 'anchor'" class="panel-body">
                <div class="anchor-form">
                  <div v-if="anchorStatus.status === 1" class="anchor-result ok">
                    <svg viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <p>您的申请已通过，当前为主播身份</p>
                  </div>
                  <div v-else-if="anchorStatus.status === 0" class="anchor-result pending">
                    <p>申请审核中，请耐心等待</p>
                    <p v-if="anchorStatus.remark" class="remark">审核备注：{{ anchorStatus.remark }}</p>
                  </div>
                  <div v-else-if="anchorStatus.status === 2" class="anchor-result reject">
                    <p>申请未通过</p>
                    <p v-if="anchorStatus.remark" class="remark">原因：{{ anchorStatus.remark }}</p>
                  </div>
                  <template v-else>
                    <div class="form-row">
                      <label>真实姓名</label>
                      <input type="text" v-model="anchorForm.realname" placeholder="请输入真实姓名">
                    </div>
                    <div class="form-row">
                      <label>身份证号</label>
                      <input type="text" v-model="anchorForm.idcard" placeholder="请输入身份证号" maxlength="18">
                    </div>
                    <div class="form-row">
                      <label>联系电话</label>
                      <input type="text" v-model="anchorForm.phone" placeholder="请输入手机号" maxlength="11">
                    </div>
                    <div class="form-row">
                      <label>申请理由</label>
                      <textarea v-model="anchorForm.reason" placeholder="简单介绍一下你自己或直播方向" rows="3"></textarea>
                    </div>
                    <div class="form-actions">
                      <button class="btn-primary" @click="submitAnchor" :disabled="anchorSubmitting">
                        {{ anchorSubmitting ? '提交中...' : '提交申请' }}
                      </button>
                    </div>
                  </template>
                </div>
              </div>

              <!-- 违规查询 -->
              <div v-if="currentTab === 'violation'" class="panel-body no-padding">
                <ViolationQuery :embedded="true" />
              </div>

              <!-- 充值中心 -->
              <div v-if="currentTab === 'recharge'" class="panel-body">
                <RechargePanel :embedded="true" />
              </div>

              <!-- 礼物商城 -->
              <div v-if="currentTab === 'shop'" class="panel-body">
                <div class="shop-page">
                  <div class="shop-tabs">
                    <span class="shop-tab" :class="{ active: shopTab === 'list' }" @click="shopTab = 'list'; fetchShopItems();">礼物商城</span>
                    <span class="shop-tab" :class="{ active: shopTab === 'bag' }" @click="shopTab = 'bag'; fetchMyItems();">我的背包</span>
                    <span class="shop-tab" :class="{ active: shopTab === 'cdkey' }" @click="shopTab = 'cdkey'; fetchCdkeyRecords();">CDKEY兑换</span>
                  </div>

                  <!-- CDKEY 兑换 -->
                  <div v-if="shopTab === 'cdkey'" class="shop-cdkey-page">
                    <div class="cdkey-form">
                      <div class="form-row">
                        <label>CDKey 兑换码</label>
                        <input type="text" v-model="shopCdkey" placeholder="掉宝获得的兑换码，输入即可兑换礼物">
                      </div>
                      <div class="form-actions">
                        <button class="btn-primary" @click="handleShopCdkey">立即兑换</button>
                      </div>
                    </div>
                    <div class="cdkey-records" v-if="cdkeyRecords.length > 0">
                      <h4>兑换记录</h4>
                      <div class="cdkey-record-item" v-for="(r, i) in cdkeyRecords" :key="r.id || r.code || i">
                        <span class="cdkey-code">{{ r.code }}</span>
                        <span class="cdkey-reward">{{ r.reward_type === 'score' ? r.reward_amount + '金币' : r.reward_type === 'money' ? '¥' + r.reward_amount : r.reward_type === 'vip_days' ? r.reward_amount + '天VIP' : r.reward_type === 'gift' ? (r.gift_name || '礼物') + ' x' + (r.gift_count || 1) : r.reward_amount }}</span>
                        <span class="cdkey-time">{{ r.createtime }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- 商城列表 -->
                  <div v-if="shopTab === 'list'" class="shop-list-page">
                    <div v-if="shopItems.length === 0" class="shop-empty">
                      <img :src="logo" alt="">
                      <p>暂无礼物</p>
                    </div>
                    <div v-else class="shop-grid">
                      <div class="shop-card" v-for="item in shopItems" :key="item.id">
                        <div class="shop-card-icon">
                          <img v-if="item.icon || item.image" :src="giftIconUrl(item.icon || item.image)" :alt="item.name" @error="handleImgErrorShop">
                          <svg v-else viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#ff9600" stroke-width="1.5">
                            <path d="M6 2L3 6v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6l-3-4H6z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
                          </svg>
                        </div>
                        <div class="shop-card-info">
                          <div class="shop-card-name">{{ item.name }}</div>
                          <div class="shop-card-desc">{{ item.desc || item.description || '精彩礼物' }}</div>
                          <div class="shop-card-bottom">
                            <span class="shop-card-price">
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#ff6a00" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 10h8"/></svg>
                              {{ item.price }}
                            </span>
                            <button class="shop-buy-btn" @click="handleBuyItem(item)">购买</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 我的背包 -->
                  <div v-if="shopTab === 'bag'" class="shop-bag-page">
                    <div v-if="myItems.length === 0" class="shop-empty">
                      <img :src="logo" alt="">
                      <p>背包空空如也，去商城购买礼物吧</p>
                      <button class="btn-primary" @click="shopTab = 'list'">去商城</button>
                    </div>
                    <div v-else class="shop-grid">
                      <div class="shop-card" v-for="item in myItems" :key="item.id">
                        <div class="shop-card-icon">
                          <img v-if="item.icon" :src="giftIconUrl(item.icon)" :alt="item.name" @error="handleImgErrorShop">
                          <svg v-else viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#ff9600" stroke-width="1.5">
                            <path d="M6 2L3 6v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6l-3-4H6z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>
                          </svg>
                        </div>
                        <div class="shop-card-info">
                          <div class="shop-card-name">{{ item.name }}</div>
                          <div class="shop-card-desc">数量：{{ item.quantity || item.count || item.num || 1 }}</div>
                          <div class="shop-card-bottom">
                            <button class="shop-use-btn" @click="handleUseItem(item)">使用</button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </template>
          </div>
        </main>
      </div>
    </div>

    <!-- 手机绑定弹窗 -->
    <div v-if="showBindModal" class="modal-overlay" @click="showBindModal = false">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">绑定手机</h3>
        <div class="form-row">
          <label>手机号</label>
          <input type="text" v-model="bindForm.account" placeholder="请输入手机号" maxlength="11">
        </div>
        <div class="form-row code-row">
          <label>验证码</label>
          <div class="code-input-wrap">
            <input type="text" v-model="bindForm.captcha" placeholder="请输入验证码" maxlength="6">
            <button class="send-code-btn" :disabled="codeCooldown > 0" @click="sendCode">
              {{ codeCooldown > 0 ? codeCooldown + 's' : '发送验证码' }}
            </button>
          </div>
        </div>
        <div class="modal-actions">
          <button class="cancel-btn" @click="showBindModal = false">取消</button>
          <button class="confirm-btn" @click="handleBind">确认绑定</button>
        </div>
      </div>
    </div>

    <!-- 提现弹窗 -->
    <div v-if="showWithdrawModal" class="modal-overlay" @click="showWithdrawModal = false">
      <div class="modal-content" @click.stop>
        <h3 class="modal-title">申请提现</h3>
        <div class="form-row">
          <label>提现金额</label>
          <input type="number" v-model="withdrawForm.amount" placeholder="可提现金额 ¥{{ commissionData.available || '0.00' }}" step="0.01" min="0">
        </div>
        <div class="form-row">
          <label>收款账号</label>
          <input type="text" v-model="withdrawForm.account" placeholder="支付宝/微信账号">
        </div>
        <div class="form-row">
          <label>真实姓名</label>
          <input type="text" v-model="withdrawForm.realname" placeholder="请输入真实姓名">
        </div>
        <div class="modal-actions">
          <button class="cancel-btn" @click="showWithdrawModal = false">取消</button>
          <button class="confirm-btn" @click="handleWithdraw">确认提现</button>
        </div>
      </div>
    </div>

    <!-- 宝箱奖励弹窗 -->
    <transition name="treasure-reward">
      <div v-if="showTreasureReward && treasureReward" class="treasure-reward-overlay" @click="closeTreasureReward">
        <div class="treasure-reward-modal" @click.stop>
          <div class="treasure-reward-close" @click="closeTreasureReward">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </div>
          <div class="treasure-reward-icon">
            <svg viewBox="0 0 24 24" width="80" height="80" fill="none" stroke="currentColor" stroke-width="1.5">
              <path d="M20 6h-2.18A3 3 0 0 0 12 3.18V3a1 1 0 0 0-2 0v.18A3 3 0 0 0 4.18 6H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2z"/>
              <polyline points="12 8 12 16"/><polyline points="8 12 16 12"/>
            </svg>
            <div class="treasure-reward-spark spark-1"></div>
            <div class="treasure-reward-spark spark-2"></div>
            <div class="treasure-reward-spark spark-3"></div>
            <div class="treasure-reward-spark spark-4"></div>
          </div>
          <h3 class="treasure-reward-title">恭喜获得</h3>
          <div class="treasure-reward-name">{{ treasureReward.gift_name || treasureReward.name || '神秘奖励' }}</div>
          <div v-if="treasureReward.cdkey" class="treasure-reward-cdkey">
            <span class="treasure-reward-cdkey-label">礼物兑换码</span>
            <div class="treasure-reward-cdkey-code" @click="copyText(treasureReward.cdkey)">{{ treasureReward.cdkey }}</div>
            <span class="treasure-reward-cdkey-tip">点击复制，前往「道具商城-CDKEY兑换」输入领取</span>
          </div>
          <div v-if="treasureReward.reward && !treasureReward.cdkey" class="treasure-reward-value">{{ treasureReward.reward }}</div>
          <div v-if="treasureReward.desc" class="treasure-reward-desc">{{ treasureReward.desc }}</div>
          <button class="btn-primary treasure-reward-confirm" @click="closeTreasureReward">收下奖励</button>
        </div>
      </div>
    </transition>

    <PageFooter />
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'; // eslint-disable-line no-unused-vars
import { useRoute, useRouter } from 'vue-router';
import PageFooter from './Footer.vue';
import FollowList from './FollowList.vue';
import WatchHistory from './WatchHistory.vue';
import NobleShop from './NobleShop.vue';
import ViolationQuery from './ViolationQuery.vue';
import RealnamePanel from './Realname.vue';
import RechargePanel from './RechargePackages.vue';
import TitlePanel from './TitlePanel.vue';
import RoomAdminPanel from './RoomAdminPanel.vue';
import CreatorPanel from './CreatorPanel.vue';
import PostFeed from './PostFeed.vue';
import { getUserInfo, updateProfile, uploadFile, changePassword, bindMobile, sendSms } from '../api/user';
import { getMyCommission, withdraw } from '../api/commission';
import { getItemList, buyItem, getMyItems, useItem } from '../api/shop';
import { getMyTreasures, openTreasure, getTreasureStatus } from '../api/treasure';
import { getExpInfo } from '../api/liveLevel';
import { submitAnchorApply, getAnchorApplyStatus } from '../api/anchor';
import { getTaskList, claimTaskReward, dailySignin } from '../api/task';
import { getMessageList, getUnreadCount, readMessage, readAllMessages, getConversations, getConversationDetail, sendMessage } from '../api/message';
import { redeemCdkey, getCdkeyRecords } from '../api/cdkey';
import { getRealnameStatus } from '../api/realname';
import { BASE_URL } from '../api/config';
import { useUserStore } from '../stores/user';
import { toast } from '../utils/toast';
import { confirmDialog } from './ConfirmDialog.vue';

export default {
  name: 'UserCenter',
  components: {
    PageFooter,
    FollowList,
    WatchHistory,
    NobleShop,
    ViolationQuery,
    RealnamePanel,
    RechargePanel,
    TitlePanel,
    RoomAdminPanel,
    CreatorPanel,
    PostFeed
  },
  setup() {
    const userStore = useUserStore();
    const route = useRoute();
    const router = useRouter();

    // 智能提示分发：根据消息内容自动选择 success/error/warning/info
    const notify = (msg, forceType) => {
      const text = String(msg == null ? '' : msg);
      if (forceType && toast[forceType]) { toast[forceType](text); return; }
      if (/成功|已发送|已提交|已开启|已绑定|已领取|已保存|已复制/.test(text)) toast.success(text);
      else if (/失败|错误|异常|不可/.test(text)) toast.error(text);
      else if (/不能为空|不能超过|请输入|请填写|不正确|不一致|至少|无效/.test(text)) toast.warning(text);
      else toast.info(text);
    };
    // 统一替换原生 alert，让全站提示走轻量 toast
    const alert = (msg) => notify(msg);

    const userInfo = ref({
      id: '', username: '', nickname: '', mobile: null, avatar: '', bio: '',
      score: 0, money: '0.00', createtime: '', logintime: '', level: 1, gender: 0
    });
    const loading = ref(false);
    const currentTab = ref('home');
    // 青少年模式：隐藏礼物/宝箱/贵族等消费入口。由后端 minor_mode 驱动，
    // 游客与未实名用户均为 0（不强制），已实名未成年恒为 1（强制）
    const minorMode = ref(false);
    // 侧边栏醒目入口卡的状态副文案所需数据
    const realnameStatus = ref(0);
    const isMinor = ref(0);
    const subTab = ref('follow');
    const cdkey = ref('');
    const shopCdkey = ref('');
    const showAllPrivileges = ref(false);

    // 任务相关
    const dailyTasks = ref([]);
    const newbieTasks = ref([]);

    // 消息相关
    const messageTab = ref('system');
    const messageList = ref([]);
    const messageUnread = ref({ system: 0, interact: 0, private: 0, total: 0 });
    const conversationList = ref([]);
    const currentConversation = ref(null);
    const conversationMessages = ref([]);
    const messageInput = ref('');
    const showConversation = ref(false);

    // CDKey 兑换记录
    const cdkeyRecords = ref([]);

    const defaultAvatarUrl = require('../assets/logo.png');

    const editForm = ref({ username: '', nickname: '', bio: '', gender: 0 });
    const basicSubTab = ref('profile');
    const accountForm = ref({ oldPwd: '', newPwd: '', confirmPwd: '' });

    const anchorForm = ref({ realname: '', idcard: '', phone: '', reason: '' });
    const anchorStatus = ref({ status: -1, remark: '' });
    const anchorSubmitting = ref(false);

    // 绑定弹窗相关
    const showBindModal = ref(false);
    const bindForm = ref({ account: '', captcha: '' });
    const codeCooldown = ref(0);
    let codeTimer = null;

    // 佣金相关
    const commissionData = ref({});
    const showWithdrawModal = ref(false);
    const withdrawForm = ref({ amount: '', account: '', realname: '' });

    // 掉宝相关
    const treasureList = ref([]);
    const treasureStatus = ref(null);
    const treasureOpening = ref(false);
    const treasureReward = ref(null);
    const showTreasureReward = ref(false);

    // 经验信息
    const expInfo = ref(null);

    // 礼物商城
    const logo = defaultAvatarUrl;
    const shopTab = ref('list');
    const shopItems = ref([]);
    const myItems = ref([]);

    const liveLevel = computed(() => {
      return Math.max(1, Number(expInfo.value?.level ?? userInfo.value.live_level) || 1);
    });

    const liveExp = computed(() => {
      return Math.max(0, Number(expInfo.value?.exp ?? userInfo.value.live_exp) || 0);
    });

    const liveNeedExp = computed(() => {
      return Math.max(1, Number(expInfo.value?.need_exp) || liveLevel.value * 200);
    });

    const levelPercent = computed(() => {
      return Math.min((liveExp.value / liveNeedExp.value) * 100, 100);
    });

    const genderIcon = computed(() => {
      if (userInfo.value.gender === 1) {
        return '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#3b82f6" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 10h8"/></svg>';
      }
      return '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="#ec4899" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v12M8 12h8"/></svg>';
    });

    const avatarUrl = computed(() => {
      if (!userInfo.value.avatar) return defaultAvatarUrl;
      if (userInfo.value.avatar.startsWith('http') || userInfo.value.avatar.startsWith('data:')) return userInfo.value.avatar;
      return `${BASE_URL}${userInfo.value.avatar}`;
    });

    const menuItems = [
      { key: 'home', text: '个人中心', group: 'account', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>' },
      { key: 'basic', text: '我的信息', group: 'account', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>' },
      { key: 'level', text: '我的等级', group: 'account', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>', badge: true },
      { key: 'task', text: '任务中心', group: 'account', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M12 8l1.5 3.5H17l-3 2.5 1 3.5-3-2.5-3 2.5 1-3.5-3-2.5h3.5L12 8z"/></svg>' },
      { key: 'treasure', text: '我的掉宝', group: 'account', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6h-2.18A3 3 0 0 0 12 3.18V3a1 1 0 0 0-2 0v.18A3 3 0 0 0 4.18 6H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2z"/></svg>' },
      { key: 'cdkey', text: 'CDKey兑换', group: 'account', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="7" width="20" height="10" rx="2"/><path d="M7 7v10M17 7v10"/></svg>' },
      { key: 'posts', text: '我的动态', group: 'interact', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M8 12h8M8 8h8M8 16h4"/><rect x="2" y="4" width="20" height="16" rx="2"/></svg>' },
      { key: 'follow_history', text: '关注与历史', subtitle: '关注了0个主播', group: 'interact', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/></svg>' },
      { key: 'messages', text: '我的消息', group: 'interact', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>' },
      { key: 'shop', text: '道具商城', group: 'interact', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6l-3-4H6z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>' },
      { key: 'noble', text: '我的贵族', group: 'anchor', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M12 2l2.4 7.2h7.6l-6 4.8 2.4 7.2-6-4.8-6 4.8 2.4-7.2-6-4.8h7.6z"/></svg>' },
      { key: 'title', text: '特权管理', group: 'anchor', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2l2.4 7.2h7.6l-6 4.8 2.4 7.2-6-4.8-6 4.8 2.4-7.2-6-4.8h7.6z"/></svg>' },
      { key: 'roomadmin', text: '我是房管', group: 'anchor', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>' },
      { key: 'anchor', text: '主播招募', group: 'anchor', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 7l-7 5 7 5V7z"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>' },
      { key: 'creator', text: '创作中心', group: 'anchor', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>' },
      { key: 'violation', text: '违规查询', group: 'anchor', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>' },
      { key: 'realname', text: '认证/青少年', group: 'security', icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M9 12l2 2 4-4"/></svg>' }
    ];

    // 青少年模式下需隐藏的消费类菜单：
    // 我的掉宝(宝箱)、CDKey兑换、道具商城(礼物)、我的贵族、特权管理、我的佣金、充值中心
    const minorHiddenTabs = [
      // 消费类：掉宝(宝箱)、CDKey兑换、道具商城(礼物)、贵族、特权、佣金、充值
      'treasure', 'cdkey', 'shop', 'noble', 'title', 'commission', 'recharge',
      // 激励/社交/主播类：等级(诱导消费冲级)、任务中心、动态、关注与历史、
      // 私信消息（未成年人陌生人社交风险）、房管、主播招募、创作中心
      'level', 'task', 'posts', 'follow_history', 'messages',
      'roomadmin', 'anchor', 'creator'
    ];

    // 左侧菜单分组（账户中心 / 内容互动 / 主播与权益 / 安全与合规）
    const menuGroups = computed(() => {
      const groups = [
        { key: 'account', title: '账户中心', keys: ['home', 'basic', 'level', 'task', 'treasure', 'cdkey'] },
        { key: 'interact', title: '内容互动', keys: ['posts', 'follow_history', 'messages', 'shop'] },
        { key: 'anchor', title: '主播与权益', keys: ['noble', 'title', 'roomadmin', 'anchor', 'creator', 'violation'] }
        // 注：'realname'（实名认证 / 青少年模式）已改为侧边栏顶部的醒目入口卡
      ];
      return groups.map(g => ({
        ...g,
        items: g.keys
          // 青少年模式：过滤掉礼物/宝箱/贵族等消费入口
          .filter(k => !(minorMode.value && minorHiddenTabs.indexOf(k) >= 0))
          .map(k => menuItems.find(i => i.key === k))
          .filter(Boolean)
      // 分组内全部被过滤后不再渲染空标题
      })).filter(g => g.items.length > 0);
    });

    const currentMenu = computed(() => menuItems.find(i => i.key === currentTab.value) || menuItems[0]);

    const allPrivileges = computed(() => {
      const list = [];
      levelPrivileges.forEach(p => {
        list.push({ lv: p.lv, text: p.text });
      });
      list.push({ lv: 'Lv20', text: '专属客服通道' });
      list.push({ lv: 'Lv25', text: '动态头像框' });
      list.push({ lv: 'Lv30', text: '至尊铭牌' });
      return list;
    });

    // 任务默认图标与配色（API 未返回时兜底）
    const defaultTaskIcon = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M12 8l1.5 3.5H17l-3 2.5 1 3.5-3-2.5-3 2.5 1-3.5-3-2.5h3.5L12 8z"/></svg>';
    const taskColorPool = ['orange', 'blue', 'green', 'pink'];
    const taskIconMap = {
      watch_live: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="18" height="12" rx="3"/><circle cx="11" cy="12" r="3" fill="currentColor"/><path d="M22 9v6"/></svg>',
      send_danmaku: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7A8.38 8.38 0 0 1 4 11.5a8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>',
      share: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>',
      follow_anchor: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>',
      complete_profile: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
      bind_mobile: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>',
      first_recharge: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><circle cx="12" cy="12" r="2"/></svg>'
    };

    const levelPrivileges = [
      { icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>', text: '等级标签', lv: 'Lv2', color: 'orange' },
      { icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 12h8M9 8h6M9 16h6"/></svg>', text: '普通灵眸号', lv: 'Lv2', color: 'blue' },
      { icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 9h8M8 12h8M8 15h5"/></svg>', text: '高级灵眸号', lv: 'Lv10', color: 'cyan' },
      { icon: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2M9 9h.01M15 9h.01"/></svg>', text: '静态表情', lv: 'Lv14', color: 'yellow' }
    ];

    const fetchUserInfo = async () => {
      loading.value = true;
      try {
        const token = localStorage.getItem('token');
        const response = await getUserInfo(token);
        if (response.data.code === 1) {
          const { userinfo } = response.data.data;
          userInfo.value = { gender: 0, ...userinfo };
          editForm.value = {
            username: userinfo?.username || '',
            nickname: userinfo?.nickname || '',
            bio: userinfo?.bio || '',
            gender: userinfo?.gender || 0
          };
        }
      } catch (error) {
        console.error('获取用户信息失败:', error);
      } finally {
        loading.value = false;
      }
    };

    // 规范化 avatar：只保留相对路径，避免存入完整 URL / base64 导致数据库字段超长
    const normalizeAvatar = (url) => {
      if (!url) return '';
      if (url.startsWith('data:')) return ''; // base64 不入库
      if (url.startsWith('http://') || url.startsWith('https://')) {
        try {
          const u = new URL(url);
          return u.pathname; // 只保留路径部分
        } catch (e) { return ''; }
      }
      return url;
    };

    const saveUserInfo = async () => {
      if (!editForm.value.username.trim()) { alert('用户名不能为空'); return; }
      if (!editForm.value.nickname.trim()) { alert('昵称不能为空'); return; }
      try {
        const response = await updateProfile({
          username: editForm.value.username.trim(),
          nickname: editForm.value.nickname.trim(),
          bio: editForm.value.bio.trim() || '',
          avatar: normalizeAvatar(userInfo.value.avatar),
          gender: editForm.value.gender
        });
        if (response.data.code === 1) {
          alert('保存成功');
          await fetchUserInfo();
          const currentUserInfo = userStore.userInfo || {};
          userStore.setUserInfo({
            ...currentUserInfo,
            username: editForm.value.username.trim(),
            nickname: editForm.value.nickname.trim(),
            bio: editForm.value.bio.trim() || '',
            gender: editForm.value.gender,
            avatar: normalizeAvatar(userInfo.value.avatar),
            token: currentUserInfo.token
          });
        } else {
          alert(response.data.msg || '保存失败');
        }
      } catch (error) {
        alert(error?.message || '保存失败，请重试');
      }
    };

    const savePassword = async () => {
      const { oldPwd, newPwd, confirmPwd } = accountForm.value;
      if (!oldPwd.trim()) { alert('请输入当前密码'); return; }
      if (newPwd.length < 6) { alert('新密码需至少 6 位'); return; }
      if (newPwd !== confirmPwd) { alert('两次输入的新密码不一致'); return; }
      try {
        const response = await changePassword(oldPwd, newPwd);
        if (response.data.code === 1) {
          alert('密码修改成功');
          accountForm.value = { oldPwd: '', newPwd: '', confirmPwd: '' };
        } else {
          alert(response.data.msg || '密码修改失败');
        }
      } catch (error) {
        alert(error?.message || '密码修改失败，请重试');
      }
    };

    const handleAvatarError = (e) => { e.target.src = defaultAvatarUrl; };
    const handleImgErrorShop = (e) => { e.target.src = defaultAvatarUrl; };
    const giftIconUrl = (url) => {
      if (!url) return '';
      if (url.startsWith('http') || url.startsWith('data:')) return url;
      return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
    };

    const handleAvatarChange = async (event) => {
      const file = event.target.files[0];
      if (!file) return;
      const formData = new FormData();
      formData.append('file', file);
      try {
        const response = await uploadFile(formData);
        if (response.data.code === 1) {
          const rawPath = response.data.data.url || response.data.data || '';
          const avatarPath = normalizeAvatar(rawPath.startsWith('/') ? rawPath : '/' + rawPath);
          const updateResponse = await updateProfile({
            username: userInfo.value.username,
            nickname: userInfo.value.nickname,
            bio: userInfo.value.bio || '',
            avatar: avatarPath
          });
          if (updateResponse.data.code === 1) {
            alert('头像上传成功');
            const updatedUserInfo = updateResponse.data.data || userInfo.value;
            userInfo.value = updatedUserInfo;
            const currentUserInfo = userStore.userInfo || {};
            userStore.setUserInfo({
              ...currentUserInfo,
              ...updatedUserInfo,
              avatar: avatarPath,
              token: currentUserInfo.token
            });
            if (!updateResponse.data.data) await fetchUserInfo();
          }
        }
      } catch (error) {
        alert('头像上传失败，请重试');
      }
    };

    const fetchAnchorStatus = async () => {
      try {
        const response = await getAnchorApplyStatus();
        if (response.code === 1) anchorStatus.value = response.data;
      } catch (error) { console.error('获取主播申请状态失败:', error); }
    };

    const submitAnchor = async () => {
      const f = anchorForm.value;
      if (!f.realname.trim()) { alert('请填写真实姓名'); return; }
      if (!/^\d{17}[\dXx]$/.test(f.idcard.trim())) { alert('请填写正确的身份证号'); return; }
      if (!/^1[3-9]\d{9}$/.test(f.phone.trim())) { alert('请填写正确的手机号'); return; }
      if (!f.reason.trim()) { alert('请填写申请理由'); return; }
      anchorSubmitting.value = true;
      try {
        const response = await submitAnchorApply({
          realname: f.realname.trim(), idcard: f.idcard.trim(),
          phone: f.phone.trim(), reason: f.reason.trim()
        });
        if (response.code === 1) {
          alert('申请已提交，等待审核');
          anchorForm.value = { realname: '', idcard: '', phone: '', reason: '' };
          await fetchAnchorStatus();
        } else { alert(response.msg || '提交失败'); }
      } catch (error) { alert(error?.message || '提交失败，请稍后重试'); }
      finally { anchorSubmitting.value = false; }
    };

    const handleCdkey = async () => {
      if (!cdkey.value.trim()) { alert('请输入兑换码'); return; }
      try {
        const res = await redeemCdkey(cdkey.value.trim());
        if (res.code === 1) {
          alert(res.msg || '兑换成功');
          cdkey.value = '';
          await fetchCdkeyRecords();
          await fetchUserInfo();
        } else {
          alert(res.msg || '兑换失败');
        }
      } catch (e) {
        alert(e.message || '兑换失败');
      }
    };

    const fetchCdkeyRecords = async () => {
      try {
        const res = await getCdkeyRecords();
        if (res.code === 1) cdkeyRecords.value = res.data?.list || res.data || [];
      } catch (e) { /* ignore */ }
    };

    // 商城面板内 CDKEY 兑换礼物
    const handleShopCdkey = async () => {
      if (!shopCdkey.value.trim()) { alert('请输入兑换码'); return; }
      try {
        const res = await redeemCdkey(shopCdkey.value.trim());
        if (res.code === 1) {
          alert(res.msg || '兑换成功');
          shopCdkey.value = '';
          await fetchCdkeyRecords();
          await fetchMyItems();
          await fetchUserInfo();
        } else {
          alert(res.msg || '兑换失败');
        }
      } catch (e) {
        alert(e.message || '兑换失败');
      }
    };

    // 复制文本（如掉宝兑换码）
    const copyText = async (text) => {
      if (!text) return;
      try {
        await navigator.clipboard.writeText(String(text));
        alert('已复制兑换码');
      } catch (e) {
        // 剪贴板不可用时回退到选中文本提示
        alert('兑换码：' + text);
      }
    };

    // 任务中心
    const fetchTasks = async () => {
      try {
        const res = await getTaskList();
        if (res.code === 1) {
          const rawData = res.data || {};
          const dailyRaw = rawData.daily || [];
          const newbieRaw = rawData.newbie || [];

          const formatTask = (t, i) => {
            const key = t.task_key || t.key || '';
            const rewardType = t.reward_type || 'score';
            const rewardAmount = t.reward_amount || 0;
            const rewardText = rewardType === 'money'
              ? ('¥' + rewardAmount)
              : (rewardAmount + '金币');
            return {
              id: t.id,
              name: t.name || t.title || '',
              desc: t.desc || t.description || '',
              done_count: Number(t.done_count || 0),
              target_count: Number(t.target_count || 0),
              claimed: !!(t.is_claimed !== undefined ? t.is_claimed : t.claimed),
              completed: !!(t.is_completed !== undefined ? t.is_completed : false),
              task_key: key,
              reward: rewardText,
              icon: t.icon || taskIconMap[key] || defaultTaskIcon,
              color: t.color || taskColorPool[i % taskColorPool.length]
            };
          };

          dailyTasks.value = dailyRaw.map((t, i) => formatTask(t, i));
          newbieTasks.value = newbieRaw.map((t, i) => formatTask(t, i));
        }
      } catch (e) { /* ignore */ }
    };

    const claimReward = async (task) => {
      try {
        const res = await claimTaskReward(task.id);
        if (res.code === 1) {
          alert(res.msg || '领取成功！');
          task.claimed = true;
          await fetchUserInfo();
          await fetchTasks();
        } else {
          alert(res.msg || '领取失败');
        }
      } catch (e) {
        alert(e.message || '领取失败');
      }
    };

    const signinDaily = async () => {
      try {
        const res = await dailySignin();
        if (res.code === 1) {
          alert(res.msg || '签到成功！');
          await fetchUserInfo();
          await fetchTasks();
        } else {
          alert(res.msg || '签到失败');
        }
      } catch (e) {
        alert(e.message || '签到失败');
      }
    };

    const goDoTask = (task) => {
      const key = task.task_key;
      if (key === 'complete_profile') {
        switchTab('basic');
      } else if (key === 'bind_mobile') {
        openBindModal();
      } else if (key === 'first_recharge') {
        switchTab('recharge');
      } else if (key === 'follow_anchor') {
        switchTab('follow_history');
        subTab.value = 'follow';
      } else if (key === 'daily_sign') {
        signinDaily();
      } else {
        // 观看直播 / 发送弹幕 / 分享 等跳转到首页直播列表
        router.push('/');
      }
    };

    // 消息中心
    const fetchMessages = async (type) => {
      try {
        const res = await getMessageList({ type });
        if (res.code === 1) messageList.value = res.data?.list || res.data || [];
      } catch (e) { /* ignore */ }
    };

    const fetchUnreadCount = async () => {
      try {
        const res = await getUnreadCount();
        if (res.code === 1) messageUnread.value = res.data || { system: 0, interact: 0, private: 0, total: 0 };
      } catch (e) { /* ignore */ }
    };

    const switchMessageTab = (type) => {
      messageTab.value = type;
      if (type === 'private') {
        fetchConversations();
      } else {
        fetchMessages(type);
      }
    };

    const fetchConversations = async () => {
      try {
        const res = await getConversations();
        if (res.code === 1) conversationList.value = res.data?.list || res.data || [];
      } catch (e) { /* ignore */ }
    };

    const openConversation = async (userId) => {
      try {
        const res = await getConversationDetail({ user_id: userId });
        if (res.code === 1) {
          const data = res.data || {};
          currentConversation.value = data.user || data.target || { id: userId, nickname: '' };
          conversationMessages.value = data.list || data.messages || [];
          showConversation.value = true;
        }
      } catch (e) { /* ignore */ }
    };

    const handleSendMessage = async () => {
      const content = messageInput.value.trim();
      if (!content) return;
      const target = currentConversation.value;
      if (!target) return;
      try {
        const res = await sendMessage(target.id, content);
        if (res.code === 1) {
          messageInput.value = '';
          await openConversation(target.id);
          await fetchConversations();
        } else {
          alert(res.msg || '发送失败');
        }
      } catch (e) {
        alert(e.message || '发送失败');
      }
    };

    const markRead = async (id) => {
      try {
        const res = await readMessage(id);
        if (res.code === 1) {
          const item = messageList.value.find(m => m.id === id);
          if (item) item.is_read = 1;
          await fetchUnreadCount();
        }
      } catch (e) { /* ignore */ }
    };

    const markAllRead = async () => {
      try {
        const res = await readAllMessages(messageTab.value);
        if (res.code === 1) {
          await fetchMessages(messageTab.value);
          await fetchUnreadCount();
        }
      } catch (e) { /* ignore */ }
    };

    const convAvatar = (conv) => {
      const a = conv.avatar || conv.user_avatar || '';
      if (!a) return defaultAvatarUrl;
      if (a.indexOf('http') === 0 || a.indexOf('data:') === 0) return a;
      return BASE_URL + (a.indexOf('/') === 0 ? a : '/' + a);
    };

    const switchTab = (tab) => {
      // 青少年模式下拦住消费类 tab：菜单已隐藏，但 URL query 或页内按钮仍可能跳进来
      if (minorMode.value && minorHiddenTabs.indexOf(tab) >= 0) {
        toast.error('青少年模式下该功能不可用');
        currentTab.value = 'home';
        return;
      }
      currentTab.value = tab;
      if (tab === 'anchor') {
        // 已通过审核的主播，点击"主播招募"直接进入开播页
        fetchAnchorStatus().then(() => {
          if (anchorStatus.value.status === 1) {
            router.push('/broadcast');
          }
        });
      }
      if (tab === 'commission') fetchCommission();
      if (tab === 'treasure') {
        fetchTreasures();
        fetchTreasureStatus();
      }
      if (tab === 'task') fetchTasks();
      if (tab === 'level') fetchExpInfo();
      if (tab === 'messages') {
        fetchUnreadCount();
        fetchMessages('system');
      }
      if (tab === 'cdkey') fetchCdkeyRecords();
      if (tab === 'shop') {
        fetchShopItems();
      }
    };

    // 绑定弹窗
    const openBindModal = () => {
      bindForm.value = { account: '', captcha: '' };
      codeCooldown.value = 0;
      if (codeTimer) { clearInterval(codeTimer); codeTimer = null; }
      showBindModal.value = true;
    };

    const sendCode = async () => {
      const account = bindForm.value.account.trim();
      if (!/^1[3-9]\d{9}$/.test(account)) { alert('请输入正确的手机号'); return; }
      try {
        const res = await sendSms(account, 'bind');
        if (res.data?.code === 1) {
          alert('验证码已发送');
          codeCooldown.value = 60;
          codeTimer = setInterval(() => {
            codeCooldown.value -= 1;
            if (codeCooldown.value <= 0) {
              clearInterval(codeTimer);
              codeTimer = null;
            }
          }, 1000);
        } else {
          alert(res?.data?.msg || '验证码发送失败');
        }
      } catch (e) {
        alert(e.message || '验证码发送失败');
      }
    };

    const handleBind = async () => {
      const { account, captcha } = bindForm.value;
      if (!account.trim()) { alert('请输入手机号'); return; }
      if (!captcha.trim()) { alert('请输入验证码'); return; }
      try {
        const res = await bindMobile(account.trim(), captcha.trim());
        if (res.data?.code === 1) {
          alert('绑定成功');
          showBindModal.value = false;
          if (codeTimer) { clearInterval(codeTimer); codeTimer = null; }
          await fetchUserInfo();
        } else {
          alert(res?.data?.msg || '绑定失败');
        }
      } catch (e) {
        alert(e.message || '绑定失败，请重试');
      }
    };

    // 佣金
    const fetchCommission = async () => {
      try {
        const res = await getMyCommission();
        if (res.code === 1) commissionData.value = res.data || {};
      } catch (e) { /* ignore */ }
    };

    const handleWithdraw = async () => {
      const f = withdrawForm.value;
      if (!f.amount || Number(f.amount) <= 0) { alert('请输入有效的提现金额'); return; }
      const available = Number(commissionData.value.available || 0);
      if (Number(f.amount) > available) { alert('提现金额不能超过可提现金额'); return; }
      if (!f.account.trim()) { alert('请输入收款账号'); return; }
      if (!f.realname.trim()) { alert('请输入真实姓名'); return; }
      try {
        const res = await withdraw({
          amount: Number(f.amount),
          account: f.account.trim(),
          realname: f.realname.trim()
        });
        if (res.code === 1) {
          alert('提现申请已提交');
          showWithdrawModal.value = false;
          withdrawForm.value = { amount: '', account: '', realname: '' };
          await fetchCommission();
        } else {
          alert(res.msg || '提现失败');
        }
      } catch (e) {
        alert(e.message || '提现失败，请重试');
      }
    };

    // 掉宝
    const fetchTreasures = async () => {
      try {
        const res = await getMyTreasures();
        if (res.code === 1) treasureList.value = res.data?.list || res.data || [];
      } catch (e) { /* ignore */ }
    };

    // 宝箱状态
    const fetchTreasureStatus = async () => {
      try {
        const res = await getTreasureStatus();
        if (res.code === 1) treasureStatus.value = res.data || null;
      } catch (e) { /* ignore */ }
    };

    // 开启宝箱
    const handleOpenTreasure = async () => {
      if (treasureOpening.value) return;
      const status = treasureStatus.value;
      const available = Number(status?.available || status?.count || 0);
      if (available <= 0) {
        alert('暂无可开启的宝箱');
        return;
      }
      treasureOpening.value = true;
      try {
        const roomId = status?.room_id || status?.roomId || undefined;
        const res = await openTreasure(roomId);
        if (res.code === 1) {
          treasureReward.value = res.data || { reward: '神秘奖励' };
          showTreasureReward.value = true;
          // 刷新状态与列表
          await fetchTreasureStatus();
          await fetchTreasures();
          await fetchUserInfo();
        } else {
          alert(res.msg || '开启失败');
        }
      } catch (e) {
        alert(e.message || '开启失败');
      } finally {
        treasureOpening.value = false;
      }
    };

    const closeTreasureReward = () => {
      showTreasureReward.value = false;
      treasureReward.value = null;
    };

    // 经验信息
    const fetchExpInfo = async () => {
      try {
        const res = await getExpInfo();
        if (res.code === 1) expInfo.value = res.data || null;
      } catch (e) { /* ignore */ }
    };

    // 礼物商城
    const fetchShopItems = async () => {
      try {
        const res = await getItemList({ limit: 100 });
        if (res.code === 1) shopItems.value = res.data?.list || res.data || [];
      } catch (e) { /* ignore */ }
    };

    const fetchMyItems = async () => {
      try {
        const res = await getMyItems();
        if (res.code === 1) myItems.value = res.data?.list || res.data || [];
      } catch (e) { /* ignore */ }
    };

    const handleBuyItem = async (item) => {
      const ok = await confirmDialog({
        title: '确认购买',
        message: `确定购买【${item.name}】？`
      });
      if (!ok) return;
      try {
        const res = await buyItem(item.id);
        if (res.code === 1) {
          alert('购买成功');
          await fetchUserInfo();
          await fetchMyItems();
        } else {
          alert(res.msg || '购买失败');
        }
      } catch (e) {
        alert(e.message || '购买失败');
      }
    };

    const handleUseItem = async (item) => {
      const targetId = await confirmDialog({
        title: '送出背包礼物',
        message: `【${item.name}】将送出到目标直播间/视频，请输入目标ID：`,
        showInput: true,
        placeholder: '请输入目标直播间/视频ID'
      });
      if (targetId === null) return; // 取消
      if (!targetId || !String(targetId).trim()) {
        alert('请填写目标直播间/视频ID后再送出礼物');
        return;
      }
      const ok = await confirmDialog({
        title: '确认送出',
        message: `确定将背包中的【${item.name}】送出？`
      });
      if (!ok) return;
      try {
        const res = await useItem(item.id, Number(String(targetId).trim()));
        if (res.code === 1) {
          const data = res.data || {};
          if (data.is_gift) {
            alert(`已送出 ${data.gift_name || '礼物'} x${Number(data.quantity) || 1}`);
          } else {
            alert('使用成功');
          }
          await fetchMyItems();
        } else {
          alert(res.msg || '使用失败');
        }
      } catch (e) {
        alert(e.message || '使用失败');
      }
    };

    const VALID_TABS = menuItems.map(i => i.key);
    const applyTabFromRoute = (tab) => {
      const map = {
        recharge: 'recharge',
        noble: 'noble',
        violation: 'violation',
        realname: 'realname',
        broadcast: 'anchor',
        follow: 'follow_history',
        history: 'follow_history',
        task: 'task',
        cdkey: 'cdkey',
        messages: 'messages'
      };
      const target = map[tab] || tab;
      // 青少年模式：URL 直接带 ?tab=shop / noble 等消费 tab 时退回首页
      if (minorMode.value && minorHiddenTabs.indexOf(target) >= 0) {
        currentTab.value = 'home';
        return;
      }
      if (target && VALID_TABS.includes(target)) {
        currentTab.value = target;
        if (tab === 'history') subTab.value = 'history';
        if (tab === 'follow') subTab.value = 'follow';
        if (target === 'anchor') {
          // 已通过审核的主播，从"主播招募"入口直接进入开播页
          fetchAnchorStatus().then(() => {
            if (anchorStatus.value.status === 1) {
              router.push('/broadcast');
            }
          });
        }
        if (target === 'commission') fetchCommission();
        if (target === 'treasure') {
          fetchTreasures();
          fetchTreasureStatus();
        }
        if (target === 'task') fetchTasks();
        if (target === 'level') fetchExpInfo();
        if (target === 'cdkey') fetchCdkeyRecords();
        if (target === 'messages') {
          fetchUnreadCount();
          fetchMessages('system');
        }
        if (target === 'shop') {
          fetchShopItems();
        }
      }
    };

    // 拉取青少年模式状态；若当前停留在受限 tab 上则退回个人中心首页
    const fetchMinorMode = () => {
      getRealnameStatus().then(res => {
        const d = res && (res.data || res);
        minorMode.value = !!(d && Number(d.minor_mode) === 1);
        realnameStatus.value = d ? Number(d.realname_status) || 0 : 0;
        isMinor.value = d ? Number(d.is_minor) || 0 : 0;
        if (minorMode.value && minorHiddenTabs.indexOf(currentTab.value) >= 0) {
          currentTab.value = 'home';
        }
      }).catch(() => { /* 异常时保持显示，不影响正常用户 */ });
    };
    const onMinorModeChanged = () => fetchMinorMode();

    // 侧边栏入口卡副文案：按实名/青少年状态给出行动指引
    const sideBannerSub = computed(() => {
      if (realnameStatus.value !== 1) return '未实名，点此完成认证';
      if (isMinor.value === 1) return '已实名（未成年）· 青少年模式保护中';
      return minorMode.value ? '青少年模式已开启' : '青少年模式未开启，点此管理';
    });

    onMounted(() => {
      fetchUserInfo();
      fetchMinorMode();
      window.addEventListener('minor-mode-changed', onMinorModeChanged);
      applyTabFromRoute(route.query.tab);
    });

    // 组件卸载前清理定时器，避免内存泄漏
    onBeforeUnmount(() => {
      if (codeTimer) { clearInterval(codeTimer); codeTimer = null; }
      window.removeEventListener('minor-mode-changed', onMinorModeChanged);
    });

    watch(() => route.query.tab, (tab) => applyTabFromRoute(tab));

    return {
      userInfo, loading, currentTab, subTab, cdkey, minorMode, sideBannerSub,
      menuItems, menuGroups, currentMenu, liveLevel, liveExp, liveNeedExp, levelPercent, genderIcon, avatarUrl,
      editForm, anchorForm, anchorStatus, anchorSubmitting,
      levelPrivileges, allPrivileges, showAllPrivileges,
      dailyTasks, newbieTasks,
      defaultAvatarUrl, basicSubTab, accountForm,
      handleAvatarError, handleImgErrorShop, giftIconUrl, handleAvatarChange,
      saveUserInfo, savePassword, submitAnchor, handleCdkey, switchTab,
      showBindModal, bindForm, codeCooldown,
      openBindModal, sendCode, handleBind,
      commissionData, showWithdrawModal, withdrawForm, fetchCommission, handleWithdraw,
      treasureList, fetchTreasures,
      treasureStatus, treasureOpening, treasureReward, showTreasureReward,
      fetchTreasureStatus, handleOpenTreasure, closeTreasureReward,
      // 经验信息
      expInfo, fetchExpInfo,
      // 任务中心
      fetchTasks, claimReward, signinDaily, goDoTask,
      // 消息中心
      messageTab, messageList, messageUnread, conversationList,
      currentConversation, conversationMessages, messageInput, showConversation,
      fetchMessages, fetchUnreadCount, switchMessageTab, fetchConversations,
      openConversation, handleSendMessage, markRead, markAllRead, convAvatar,
      // CDKey 兑换记录
      cdkeyRecords, fetchCdkeyRecords,
      // 礼物商城
      logo, shopTab, shopItems, myItems, shopCdkey,
      fetchShopItems, fetchMyItems, handleBuyItem, handleUseItem,
      handleShopCdkey, copyText
    };
  }
};
</script>

<style scoped>
.user-center {
  min-height: 100vh;
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
}

.container {
  flex: 1;
  width: 100%;
}

.main-content {
  display: flex;
  max-width: 1600px;
  margin: 0 auto;
  padding: 24px 4% 40px;
  gap: 20px;
}

/* 左侧菜单 */
.side-menu {
  width: 240px;
  flex-shrink: 0;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  overflow: hidden;
  height: fit-content;
}
/* 实名认证 / 青少年模式 醒目入口卡 */
.side-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 14px 14px 6px;
  padding: 13px 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, #ff9600, #ff6a00);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(255, 106, 0, 0.26);
  transition: transform .18s ease, box-shadow .18s ease;
}
.side-banner:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(255, 106, 0, 0.34);
}
.side-banner.active {
  outline: 2px solid #ff6a00;
  outline-offset: 1px;
}
.sb-icon {
  flex: 0 0 auto;
}
.sb-text {
  flex: 1 1 auto;
  min-width: 0;
}
.sb-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}
.sb-sub {
  font-size: 12px;
  opacity: .92;
  margin-top: 2px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sb-arrow {
  flex: 0 0 auto;
  font-size: 18px;
  line-height: 1;
  opacity: .9;
}
.menu-header {
  padding: 18px 20px 12px;
  font-size: 18px;
  font-weight: 800;
  color: #333;
}
.menu-list {
  padding: 4px 0 12px;
}
.menu-group {
  margin-top: 6px;
}
.menu-group:first-child {
  margin-top: 0;
}
.menu-group-title {
  padding: 14px 20px 6px;
  font-size: 12px;
  font-weight: 700;
  color: #999;
  letter-spacing: 1px;
  user-select: none;
}
.menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px 12px 20px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  color: #666;
}
.menu-item:hover {
  color: #ff6a00;
  background: #fff8f0;
}
.menu-item.active {
  color: #ff6a00;
  background: #fff2e0;
}
.menu-item.active::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #ff6a00, #ff9600);
}
.menu-item.soon {
  opacity: 0.6;
}
.menu-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}
.menu-icon svg {
  width: 18px;
  height: 18px;
}
.menu-text {
  font-size: 14px;
  flex: 1;
}
.menu-badge {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
}

/* 右侧内容 */
.content-area {
  flex: 1;
  min-width: 0;
}

/* 用户信息卡片 */
.user-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  margin-bottom: 16px;
}
.user-card-left {
  display: flex;
  align-items: center;
  gap: 18px;
}
.avatar-wrap {
  position: relative;
  width: 86px;
  height: 86px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #ffe4cc;
}
.avatar-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatar-edit {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 28px;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s;
  cursor: pointer;
  color: #fff;
}
.avatar-wrap:hover .avatar-edit { opacity: 1; }
.avatar-edit input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.user-meta h2 {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #333;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.nickname-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.gender-icon {
  display: flex;
  align-items: center;
}
.edit-name {
  color: #ff9600;
  cursor: pointer;
  display: flex;
  align-items: center;
}
.level-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.level-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
}
.level-tag {
  font-size: 12px;
  color: #ff6a00;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 10px;
  border: 1px solid #ffb27a;
  border-radius: 12px;
  background: #fff8f2;
  transition: all 0.2s;
}
.level-tag:hover { background: #ffe9d6; color: #e55200; }
.progress-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 260px;
}
.progress-bar {
  flex: 1;
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 4px;
}
.progress-text {
  font-size: 12px;
  color: #999;
  min-width: 50px;
  text-align: right;
}

.user-card-right {
  text-align: center;
  padding-left: 24px;
  border-left: 1px solid #f0f0f0;
}
.assets-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
  color: #999;
  margin-bottom: 6px;
}
.assets-value {
  font-size: 24px;
  font-weight: 800;
  color: #ff6a00;
  margin-bottom: 10px;
}
.recharge-btn {
  padding: 6px 24px;
  border: none;
  border-radius: 16px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

/* 快捷入口 */
.quick-entry {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.entry-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}
.entry-item:hover { transform: translateY(-2px); }
.entry-item span {
  font-size: 13px;
  color: #666;
}
.entry-icon {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.entry-icon.orange { background: linear-gradient(135deg, #ff6a00, #ff9600); }
.entry-icon.blue { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.entry-icon.green { background: linear-gradient(135deg, #10b981, #34d399); }
.entry-icon.purple { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.entry-icon.red { background: linear-gradient(135deg, #ef4444, #f87171); }

/* 面板 */
.panel {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  min-height: 500px;
}
.panel-header {
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}
.panel-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: #333;
}
.panel-header p {
  margin: 6px 0 0;
  font-size: 13px;
  color: #999;
}
.panel-body {
  padding: 24px;
}
.panel-body.no-padding {
  padding: 0;
}
.panel-loading {
  padding: 80px;
  text-align: center;
  color: #999;
}

/* 表单 */
.info-form, .anchor-form, .cdkey-form {
  max-width: 520px;
}

/* CDKey 兑换记录 */
.cdkey-records {
  margin-top: 32px;
  max-width: 640px;
}
.cdkey-records h4 {
  margin: 0 0 14px;
  font-size: 15px;
  font-weight: 700;
  color: #333;
  padding-left: 10px;
  border-left: 3px solid #ff9600;
}
.cdkey-record-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 10px;
  margin-bottom: 10px;
  transition: all 0.2s;
}
.cdkey-record-item:hover {
  background: #fff5eb;
  transform: translateX(2px);
}
.cdkey-code {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
}
.cdkey-reward {
  font-size: 14px;
  font-weight: 700;
  color: #ff6a00;
  white-space: nowrap;
}
.cdkey-time {
  font-size: 12px;
  color: #999;
  white-space: nowrap;
}
.form-row {
  margin-bottom: 20px;
}
.form-row label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}
.form-row input,
.form-row textarea {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: all 0.25s;
}
.form-row input:focus,
.form-row textarea:focus {
  border-color: #ff9600;
  box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
}
.form-static {
  padding: 11px 14px;
  background: #f8f8f8;
  border-radius: 8px;
  color: #999;
  font-size: 14px;
}
.avatar-row .avatar-upload {
  position: relative;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid #ffe4cc;
}
.avatar-row .avatar-upload img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.avatar-upload-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s;
}
.avatar-row .avatar-upload:hover .avatar-upload-mask {
  opacity: 1;
}
.avatar-upload-mask input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.form-actions {
  margin-top: 28px;
}
.btn-primary {
  padding: 0 28px;
  height: 40px;
  border: none;
  border-radius: 20px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
}
.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 106, 0, 0.3);
}
.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 个人中心首页 */
.home-dashboard {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(90deg, #fff8f0, #fff);
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #fff0e0;
}
.home-welcome {
  display: flex;
  align-items: center;
  gap: 16px;
}
.home-avatar {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ffe4cc;
}
.home-name {
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}
.home-level {
  display: flex;
  align-items: center;
  gap: 10px;
}
.home-actions {
  display: flex;
  gap: 8px;
}
.home-action {
  width: 148px;
  min-height: 58px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid #ececec;
  background: #fff;
  color: #333;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s, background 0.2s;
}
.home-action:hover {
  border-color: #ffc28e;
  background: #fffaf6;
}
.home-action-icon {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  flex: none;
  color: #ff6a00;
  background: #fff2e8;
}
.home-action > span:last-child {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.home-action b {
  font-size: 13px;
  font-weight: 700;
}
.home-action small {
  margin-top: 3px;
  font-size: 11px;
  color: #999;
  white-space: nowrap;
}

/* 等级页 */
.level-page {}
.level-header {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
  background: #fafafa;
  border-radius: 12px;
  margin-bottom: 24px;
}
.level-avatar {
  width: 78px;
  height: 78px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ffe4cc;
}
.level-header-info {
  flex: 1;
}
.lh-name {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #333;
  margin-bottom: 8px;
}
.lh-gender {
  display: flex;
  align-items: center;
}
.lh-edit {
  display: flex;
  align-items: center;
  color: #ff9600;
  cursor: pointer;
  transition: transform 0.2s;
}
.lh-edit:hover {
  transform: scale(1.1);
}
.lh-level {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.lh-level-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: linear-gradient(90deg, #10b981, #34d399);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 12px;
}
.lh-level-badge svg {
  width: 11px;
  height: 11px;
}
.lh-level-tag {
  font-size: 12px;
  color: #999;
  cursor: pointer;
}
.lh-level-tag:hover {
  color: #ff6a00;
}
.lh-progress-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 320px;
}
.lh-progress-row .progress-bar {
  flex: 1;
  height: 8px;
  background: #eee;
  border-radius: 4px;
  overflow: hidden;
}
.lh-progress-row .progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 4px;
}
.lh-level-text {
  font-size: 11px;
  color: #999;
}
.lh-progress-sub {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  width: 320px;
  text-align: center;
}

/* 经验来源明细 */
.exp-detail-card {
  margin: 20px 0;
  padding: 20px;
  background: linear-gradient(135deg, #fff8f0, #fff);
  border: 1px solid #ffe8d6;
  border-radius: 12px;
}
.exp-detail-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
}
.exp-detail-total {
  margin-left: auto;
  font-size: 13px;
  color: #ff6a00;
  font-weight: 600;
}
.exp-detail-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.exp-detail-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #fff;
  border-radius: 10px;
  border: 1px solid #f5f0eb;
  transition: all 0.2s;
}
.exp-detail-item:hover {
  border-color: #ffe4cc;
  transform: translateX(2px);
}
.exp-detail-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.exp-detail-icon.watch {
  background: linear-gradient(135deg, #ff6a00, #ff9600);
}
.exp-detail-icon.gift {
  background: linear-gradient(135deg, #f59e0b, #fbbf24);
}
.exp-detail-icon.interact {
  background: linear-gradient(135deg, #ef4444, #f87171);
}
.exp-detail-icon.task {
  background: linear-gradient(135deg, #8b5cf6, #a78bfa);
}
.exp-detail-info {
  flex: 1;
}
.exp-detail-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}
.exp-detail-desc {
  font-size: 12px;
  color: #999;
}
.exp-detail-value {
  font-size: 18px;
  font-weight: 800;
  color: #ff6a00;
  white-space: nowrap;
}
.exp-detail-tip {
  margin-top: 14px;
  padding: 10px 14px;
  background: #fff5eb;
  border-radius: 8px;
  font-size: 12px;
  color: #ff6a00;
  line-height: 1.6;
}

.level-section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #f0f0f0;
}
.level-all {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13px;
  color: #999;
  font-weight: normal;
  cursor: pointer;
  transition: color 0.2s;
}
.level-all:hover {
  color: #ff6a00;
}
.level-all svg {
  width: 12px;
  height: 12px;
}
.level-privileges {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.lp-item {
  width: 100px;
  text-align: center;
}
.lp-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 10px;
  color: #fff;
}
.lp-icon svg {
  width: 26px;
  height: 26px;
}
.lp-icon.orange { background: linear-gradient(135deg, #ff6a00, #ff9600); }
.lp-icon.blue { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.lp-icon.purple { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.lp-icon.pink { background: linear-gradient(135deg, #ec4899, #f472b6); }
.lp-icon.cyan { background: linear-gradient(135deg, #06b6d4, #22d3ee); }
.lp-icon.yellow { background: linear-gradient(135deg, #eab308, #facc15); }
.lp-icon.green { background: linear-gradient(135deg, #10b981, #34d399); }
.lp-text {
  font-size: 13px;
  color: #666;
  margin-bottom: 4px;
}
.lp-lv {
  font-size: 12px;
  color: #999;
}

/* 全部特权弹窗 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}
.modal-content {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  width: 420px;
  max-width: 90vw;
  max-height: 80vh;
  overflow-y: auto;
}
.privileges-modal h3 {
  margin: 0 0 16px;
  font-size: 17px;
  color: #333;
}
.privilege-level-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}
.pl-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #fafafa;
  border-radius: 8px;
}
.pl-lv {
  font-size: 12px;
  color: #ff6a00;
  font-weight: 700;
  min-width: 40px;
}
.pl-text {
  font-size: 14px;
  color: #666;
}

/* 子标签 */
.sub-tabs {
  display: flex;
  gap: 24px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 20px;
}
.sub-tab {
  padding-bottom: 10px;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  position: relative;
}
.sub-tab.active {
  color: #ff6a00;
  font-weight: 700;
}
.sub-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 2px;
}

/* 消息 */
.message-layout {
  display: flex;
  flex-direction: column;
  min-height: 420px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  overflow: hidden;
}
.message-list {
  width: 260px;
  border-right: 1px solid #f0f0f0;
  background: #fafafa;
}
.msg-search {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
}
.msg-search input {
  width: 100%;
  padding: 8px 28px 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 16px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
}
.msg-search svg {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}
.msg-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s;
}
.msg-item:hover, .msg-item.active {
  background: #fff2e0;
}
.msg-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}
.msg-avatar.green { background: #34d399; }
.msg-avatar.orange { background: #ff9600; }
.msg-avatar.blue { background: #60a5fa; }
.msg-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}
.msg-preview {
  font-size: 12px;
  color: #999;
}
.message-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #999;
  gap: 12px;
}
.message-empty img {
  width: 80px;
  opacity: 0.6;
}

/* 消息标签栏 */
.message-tabs {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
}
.message-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  border-radius: 16px;
  transition: all 0.2s;
}
.message-tab:hover {
  color: #ff6a00;
  background: #fff2e0;
}
.message-tab.active {
  color: #fff;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  font-weight: 700;
}
.message-readall {
  margin-left: auto;
  font-size: 13px;
  color: #ff6a00;
  cursor: pointer;
  transition: opacity 0.2s;
}
.message-readall:hover {
  opacity: 0.7;
}
.msg-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: #ff4d4f;
  color: #fff;
  font-size: 11px;
  font-style: normal;
  font-weight: 700;
  line-height: 1;
}
.message-tab.active .msg-badge {
  background: #fff;
  color: #ff6a00;
}
.msg-info {
  flex: 1;
  min-width: 0;
}
.msg-avatar-img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

/* 系统通知 / 互动通知列表 */
.message-notice {
  flex: 1;
  overflow-y: auto;
  padding: 12px 16px;
}
.msg-notice-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.msg-notice-item {
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 10px;
  border-left: 3px solid #e0e0e0;
  cursor: pointer;
  transition: all 0.2s;
}
.msg-notice-item:hover {
  background: #fff5eb;
}
.msg-notice-item.unread {
  background: #fff5eb;
  border-left-color: #ff6a00;
}
.msg-notice-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}
.msg-notice-content {
  font-size: 13px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 6px;
}
.msg-notice-time {
  font-size: 12px;
  color: #999;
}
.msg-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
  flex-shrink: 0;
}

/* 私信会话 */
.message-body {
  flex: 1;
  display: flex;
  min-height: 360px;
}
.message-detail {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.conv-header {
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 700;
  color: #333;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
}
.conv-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fafafa;
}
.conv-msg {
  max-width: 70%;
  align-self: flex-start;
}
.conv-msg span {
  display: inline-block;
  padding: 10px 14px;
  background: #fff;
  border-radius: 12px 12px 12px 4px;
  font-size: 14px;
  color: #333;
  word-break: break-word;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.conv-msg.self {
  align-self: flex-end;
}
.conv-msg.self span {
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  border-radius: 12px 12px 4px 12px;
}
.conv-input {
  display: flex;
  gap: 10px;
  padding: 12px 16px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}
.conv-input input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid #e5e5e5;
  border-radius: 20px;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: all 0.25s;
}
.conv-input input:focus {
  border-color: #ff9600;
  box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
}
.conv-input .btn-primary {
  height: 40px;
  padding: 0 22px;
}

/* 财产 */
.assets-overview {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(240px, 1fr);
  gap: 16px;
}
.asset-balance-panel,
.asset-score-panel {
  border: 1px solid #ececec;
  background: #fff;
  padding: 24px;
}
.asset-balance-panel {
  border-left: 4px solid #ff6a00;
}
.asset-balance-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}
.asset-eyebrow {
  color: #777;
  font-size: 13px;
}
.asset-main-value {
  margin-top: 8px;
  color: #202020;
  font-size: 34px;
  line-height: 1;
  font-weight: 800;
}
.asset-note {
  margin: 18px 0 0;
  color: #888;
  font-size: 12px;
  line-height: 1.7;
}
.asset-score-panel {
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  gap: 12px;
}
.asset-score-icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  color: #d48a00;
  background: #fff7df;
}
.asset-score-value {
  margin-top: 5px;
  font-size: 26px;
  font-weight: 800;
  color: #333;
}
.asset-link,
.asset-guides button {
  border: 0;
  background: none;
  color: #ff6a00;
  font-size: 12px;
  cursor: pointer;
}
.asset-guides {
  margin-top: 20px;
  border-top: 1px solid #eee;
}
.asset-guides > div {
  min-height: 54px;
  display: grid;
  grid-template-columns: 100px 1fr auto;
  align-items: center;
  gap: 16px;
  border-bottom: 1px solid #f2f2f2;
  font-size: 13px;
}
.asset-guides b {
  color: #333;
}
.asset-guides span {
  color: #888;
}

/* 主播 */
.anchor-result {
  text-align: center;
  padding: 40px 20px;
}
.anchor-result svg {
  margin-bottom: 14px;
}
.anchor-result.ok { color: #10b981; }
.anchor-result.pending { color: #ff9600; }
.anchor-result.reject { color: #ef4444; }
.anchor-result .remark {
  margin-top: 10px;
  font-size: 13px;
  color: #999;
}

/* 空状态 */
.coming-soon {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: #999;
}
.coming-soon img {
  width: 80px;
  opacity: 0.5;
  margin-bottom: 16px;
}
.coming-soon h4 {
  margin: 0 0 8px;
  color: #666;
}

/* 任务中心 */
.task-center {}
.task-section {
  margin-bottom: 28px;
}
.task-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin: 0 0 14px;
  padding-left: 10px;
  border-left: 3px solid #ff9600;
}
.task-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.task-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #fafafa;
  border-radius: 10px;
  transition: background 0.2s;
}
.task-item:hover {
  background: #fff8f0;
}
.task-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.task-icon.orange { background: linear-gradient(135deg, #ff6a00, #ff9600); }
.task-icon.blue { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.task-icon.green { background: linear-gradient(135deg, #10b981, #34d399); }
.task-icon.pink { background: linear-gradient(135deg, #ec4899, #f472b6); }
.task-icon.purple { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.task-info {
  flex: 1;
}
.task-name {
  font-size: 14px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}
.task-desc {
  font-size: 13px;
  color: #999;
  margin-bottom: 6px;
}
.task-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}
.task-progress-bar {
  flex: 1;
  max-width: 180px;
  height: 6px;
  background: #eee;
  border-radius: 3px;
  overflow: hidden;
}
.task-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 3px;
}
.task-progress-text {
  font-size: 12px;
  color: #999;
}
.task-btn {
  padding: 8px 18px;
  border: none;
  border-radius: 16px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
}
.task-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(255, 106, 0, 0.3);
}
.task-btn.done {
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
}
.task-btn.reward {
  background: linear-gradient(90deg, #ff9600, #ffc04d);
  box-shadow: 0 2px 8px rgba(255, 150, 0, 0.35);
  animation: task-reward-pulse 1.6s ease-in-out infinite;
}
@keyframes task-reward-pulse {
  0%, 100% { box-shadow: 0 2px 8px rgba(255, 150, 0, 0.3); }
  50% { box-shadow: 0 4px 14px rgba(255, 106, 0, 0.55); }
}

/* 我的信息页 */
.basic-page {}
.basic-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(90deg, #fff5eb, #fff);
  border: 1px solid #ffe8d6;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
}
.basic-user {
  display: flex;
  align-items: center;
  gap: 18px;
}
.basic-avatar-wrap {
  position: relative;
  width: 86px;
  height: 86px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid #ffd8b2;
  flex-shrink: 0;
}
.basic-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.basic-avatar-edit {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.45);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.25s;
  cursor: pointer;
}
.basic-avatar-wrap:hover .basic-avatar-edit { opacity: 1; }
.basic-avatar-edit input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.basic-meta {}
.basic-name {
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}
.basic-id {
  font-size: 13px;
  color: #999;
  margin-bottom: 10px;
}
.basic-level {
  display: flex;
  align-items: center;
  gap: 8px;
}
.basic-level-tag {
  font-size: 12px;
  color: #ff6a00;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 10px;
  border: 1px solid #ffb27a;
  border-radius: 12px;
  background: #fff8f2;
  transition: all 0.2s;
}
.basic-level-tag:hover { background: #ffe9d6; color: #e55200; }
.basic-assets {
  display: flex;
  gap: 24px;
  padding-left: 24px;
  border-left: 1px solid #ffe8d6;
}
.basic-asset {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 80px;
}
.basic-asset span {
  font-size: 12px;
  color: #999;
}
.basic-asset b {
  font-size: 20px;
  color: #ff6a00;
  font-weight: 700;
}

.basic-tabs {
  display: flex;
  gap: 32px;
  border-bottom: 1px solid #f0f0f0;
  margin-bottom: 24px;
}
.basic-tab {
  padding-bottom: 12px;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}
.basic-tab:hover { color: #ff6a00; }
.basic-tab.active {
  color: #ff6a00;
  font-weight: 700;
}
.basic-tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 2px;
}

.basic-section {}
.basic-section-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
  padding-left: 10px;
  border-left: 3px solid #ff9600;
}
.info-form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0 24px;
}
.info-form-grid .form-row:nth-last-child(1):nth-child(odd) {
  grid-column: 1 / -1;
}
.basic-section .form-actions {
  margin-top: 28px;
}
.gender-choices {
  display: flex;
  gap: 12px;
}
.gender-choices span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 9px 18px;
  border: 1px solid #e5e5e5;
  border-radius: 20px;
  font-size: 14px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
}
.gender-choices span:hover { border-color: #ff9600; color: #ff6a00; }
.gender-choices span.active {
  border-color: #ff9600;
  background: #fff5eb;
  color: #ff6a00;
  font-weight: 600;
}

.security-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 10px;
}
.security-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #fafafa;
  border-radius: 10px;
  transition: background 0.2s;
}
.security-item:hover { background: #fff5eb; }
.security-icon {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}
.security-icon.blue { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.security-icon.green { background: linear-gradient(135deg, #10b981, #34d399); }
.security-info { flex: 1; }
.security-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}
.security-desc {
  font-size: 13px;
  color: #999;
}
.security-btn {
  padding: 7px 20px;
  border: 1px solid #ff9600;
  background: #fff;
  color: #ff9600;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
}
.security-btn:hover {
  background: #ff9600;
  color: #fff;
}

.more-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
.more-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 22px 12px;
  background: #fafafa;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.25s;
}
.more-item:hover {
  background: #fff5eb;
  transform: translateY(-3px);
  box-shadow: 0 6px 16px rgba(255,106,0,0.12);
}
.more-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
}
.more-icon.orange { background: linear-gradient(135deg, #ff6a00, #ff9600); }
.more-icon.blue { background: linear-gradient(135deg, #3b82f6, #60a5fa); }
.more-icon.green { background: linear-gradient(135deg, #10b981, #34d399); }
.more-icon.purple { background: linear-gradient(135deg, #8b5cf6, #a78bfa); }
.more-name {
  font-size: 15px;
  font-weight: 600;
  color: #333;
}
.more-desc {
  font-size: 12px;
  color: #999;
  text-align: center;
}

/* 等级页优化 */
.level-header {
  background: linear-gradient(90deg, #fff5eb, #fff);
  border: 1px solid #ffe8d6;
}
.lh-level-badge {
  background: linear-gradient(90deg, #ff6a00, #ff9600);
}
.lh-level-tag {
  background: #fff0e3;
  color: #ff6a00;
  padding: 3px 10px;
  border-radius: 12px;
  font-weight: 600;
  border: 1px solid #ffcba3;
  cursor: pointer;
  transition: all 0.2s;
}
.lh-level-tag:hover {
  background: #ffe3cc;
  color: #e55200;
}
.level-privileges {
  gap: 16px;
}
.lp-item {
  width: calc(12.5% - 14px);
  min-width: 90px;
  padding: 16px 10px;
  background: #fafafa;
  border-radius: 12px;
  transition: all 0.25s;
}
.lp-item:hover {
  background: #fff5eb;
  transform: translateY(-3px);
}
.lp-icon {
  width: 50px;
  height: 50px;
}
.lp-text {
  font-weight: 600;
}

@media (max-width: 900px) {
  .main-content { flex-direction: column; }
  .side-menu { width: 100%; }
  .user-card { flex-direction: column; align-items: flex-start; gap: 20px; }
  .user-card-right { border-left: none; padding-left: 0; text-align: left; }
  .quick-entry { flex-wrap: wrap; }
  .message-layout { flex-direction: column; }
  .message-body { flex-direction: column; }
  .message-list { width: 100%; border-right: none; border-bottom: 1px solid #f0f0f0; }
  .cdkey-record-item { flex-wrap: wrap; gap: 8px; }
  .basic-header { flex-direction: column; align-items: flex-start; gap: 20px; }
  .basic-assets { border-left: none; padding-left: 0; }
  .info-form-grid { grid-template-columns: 1fr; }
  .more-grid { grid-template-columns: repeat(2, 1fr); }
  .lp-item { width: calc(25% - 12px); }
  .commission-stats { grid-template-columns: repeat(2, 1fr); }
}

/* 佣金页 */
.commission-page {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.commission-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
}
.comm-stat-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  transition: all 0.25s;
}
.comm-stat-card:hover {
  border-color: #ffe4cc;
  box-shadow: 0 4px 16px rgba(255, 150, 0, 0.08);
}
.comm-stat-value {
  font-size: 22px;
  font-weight: 800;
  color: #ff6a00;
  margin-bottom: 6px;
}
.comm-stat-label {
  font-size: 13px;
  color: #999;
}
.commission-page .btn-primary {
  align-self: flex-start;
}
.comm-records h4 {
  margin: 0 0 12px;
  font-size: 15px;
  font-weight: 700;
  color: #333;
}
.empty-text {
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

/* 通用数据表 */
.data-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
}
.data-table th,
.data-table td {
  padding: 12px 16px;
  text-align: left;
  font-size: 13px;
  color: #666;
  border-bottom: 1px solid #f0f0f0;
}
.data-table th {
  background: #fafafa;
  font-weight: 700;
  color: #333;
}
.data-table tr:last-child td {
  border-bottom: none;
}
.data-table tr:hover td {
  background: #fff8f0;
}

/* 掉宝页 */
.treasure-page {}
.treasure-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 14px;
}
.treasure-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  transition: all 0.25s;
}
.treasure-card:hover {
  border-color: #ffe4cc;
  box-shadow: 0 4px 16px rgba(255, 150, 0, 0.08);
  transform: translateY(-2px);
}
.treasure-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
  flex-shrink: 0;
}
.treasure-info {
  flex: 1;
}
.treasure-name {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin-bottom: 4px;
}
.treasure-reward {
  font-size: 13px;
  color: #ff6a00;
  font-weight: 600;
  margin-bottom: 4px;
}
.treasure-time {
  font-size: 12px;
  color: #999;
}

/* 宝箱状态卡 */
.treasure-section-title {
  font-size: 15px;
  font-weight: 700;
  color: #333;
  margin: 24px 0 14px;
  padding-left: 10px;
  border-left: 3px solid #ff9600;
}
.treasure-status-card {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8f9fa, #fff);
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  margin-bottom: 8px;
  transition: all 0.3s;
}
.treasure-status-card.ready {
  background: linear-gradient(135deg, #fff5eb, #fff);
  border-color: #ffd8b2;
  box-shadow: 0 4px 16px rgba(255, 106, 0, 0.1);
}
.treasure-status-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  color: #999;
  flex-shrink: 0;
}
.treasure-status-card.ready .treasure-status-icon {
  background: linear-gradient(135deg, #ff6a00, #ff9600);
  color: #fff;
}
.treasure-status-icon.shake {
  animation: treasure-shake 1.6s ease-in-out infinite;
}
@keyframes treasure-shake {
  0%, 100% { transform: rotate(0deg); }
  25% { transform: rotate(-6deg); }
  75% { transform: rotate(6deg); }
}
.treasure-status-info {
  flex: 1;
}
.treasure-status-title {
  font-size: 16px;
  font-weight: 700;
  color: #333;
  margin-bottom: 6px;
}
.treasure-status-desc {
  font-size: 14px;
  color: #666;
}
.treasure-status-count {
  color: #ff6a00;
  font-size: 18px;
  font-weight: 800;
  margin: 0 4px;
}
.treasure-status-next {
  font-size: 12px;
  color: #999;
}
.treasure-status-tip {
  margin-top: 6px;
  font-size: 12px;
  color: #ff9600;
}
.treasure-open-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 12px 28px;
  border: none;
  border-radius: 24px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(255, 106, 0, 0.3);
}
.treasure-open-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(255, 106, 0, 0.4);
}
.treasure-open-btn:disabled {
  background: #e0e0e0;
  color: #999;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}
.loading-icon {
  animation: loading-spin 0.8s linear infinite;
}
@keyframes loading-spin {
  to { transform: rotate(360deg); }
}

/* 宝箱奖励弹窗 */
.treasure-reward-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
}
.treasure-reward-modal {
  position: relative;
  width: 360px;
  max-width: 90vw;
  padding: 40px 24px 28px;
  background: linear-gradient(180deg, #fff5eb, #fff);
  border-radius: 20px;
  box-shadow: 0 12px 40px rgba(255, 106, 0, 0.25);
  text-align: center;
  overflow: hidden;
}
.treasure-reward-close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(0,0,0,0.05);
  color: #999;
  cursor: pointer;
  transition: all 0.2s;
}
.treasure-reward-close:hover {
  background: rgba(0,0,0,0.1);
  color: #666;
}
.treasure-reward-icon {
  position: relative;
  width: 120px;
  height: 120px;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff6a00;
  animation: treasure-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes treasure-pop {
  0% { transform: scale(0); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}
.treasure-reward-spark {
  position: absolute;
  width: 8px;
  height: 8px;
  background: #ff9600;
  border-radius: 50%;
  opacity: 0;
  animation: spark-fly 1s ease-out infinite;
}
.spark-1 { top: 10%; left: 20%; animation-delay: 0s; }
.spark-2 { top: 15%; right: 15%; animation-delay: 0.2s; background: #ff6a00; }
.spark-3 { bottom: 20%; left: 10%; animation-delay: 0.4s; background: #fbbf24; }
.spark-4 { bottom: 15%; right: 20%; animation-delay: 0.6s; background: #f59e0b; }
@keyframes spark-fly {
  0% { transform: translate(0, 0) scale(0); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translate(20px, -20px) scale(1.5); opacity: 0; }
}
.treasure-reward-title {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  color: #666;
}
.treasure-reward-name {
  font-size: 24px;
  font-weight: 800;
  color: #ff6a00;
  margin-bottom: 8px;
}
.treasure-reward-value {
  font-size: 16px;
  color: #ff9600;
  font-weight: 600;
  margin-bottom: 8px;
}
.treasure-reward-desc {
  font-size: 13px;
  color: #999;
  margin-bottom: 20px;
}
.treasure-reward-cdkey {
  margin-bottom: 14px;
}
.treasure-reward-cdkey-label {
  display: block;
  font-size: 13px;
  color: #999;
  margin-bottom: 6px;
}
.treasure-reward-cdkey-code {
  display: inline-block;
  padding: 10px 22px;
  background: linear-gradient(135deg, #fff7ec, #ffe9d0);
  border: 1px dashed #ff9600;
  border-radius: 10px;
  font-family: 'Courier New', monospace;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 2px;
  color: #ff6a00;
  cursor: pointer;
  user-select: all;
  transition: transform 0.2s;
}
.treasure-reward-cdkey-code:hover {
  transform: scale(1.04);
}
.treasure-reward-cdkey-tip {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #bbb;
}
.treasure-reward-confirm {
  width: 100%;
  height: 44px;
}

/* 宝箱奖励弹窗动画 */
.treasure-reward-enter-active {
  transition: all 0.3s ease-out;
}
.treasure-reward-leave-active {
  transition: all 0.2s ease-in;
}
.treasure-reward-enter-from {
  opacity: 0;
}
.treasure-reward-enter-from .treasure-reward-modal {
  transform: scale(0.8);
}
.treasure-reward-leave-to {
  opacity: 0;
}
.treasure-reward-leave-to .treasure-reward-modal {
  transform: scale(0.9);
}

/* 绑定弹窗内部样式 */
.modal-content .modal-title {
  font-size: 17px;
  font-weight: 700;
  color: #333;
  margin: 0 0 16px;
}
.modal-content .form-row {
  margin-bottom: 16px;
}
.modal-content .form-row label {
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 6px;
}
.modal-content .form-row input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: all 0.25s;
}
.modal-content .form-row input:focus {
  border-color: #ff9600;
  box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
}
.modal-content .form-row.code-row {
  margin-bottom: 20px;
}
.code-input-wrap {
  display: flex;
  gap: 10px;
}
.code-input-wrap input {
  flex: 1;
  width: auto;
  padding: 10px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  box-sizing: border-box;
  transition: all 0.25s;
}
.modal-content .code-input-wrap input {
  flex: 1;
  width: auto;
}
.modal-content .code-input-wrap input:focus {
  border-color: #ff9600;
  box-shadow: 0 0 0 3px rgba(255, 150, 0, 0.1);
}
.send-code-btn {
  padding: 0 16px;
  border: 1px solid #ff9600;
  background: #fff;
  color: #ff6a00;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}
.send-code-btn:hover:not(:disabled) {
  background: #ff9600;
  color: #fff;
}
.send-code-btn:disabled {
  border-color: #ddd;
  color: #999;
  cursor: not-allowed;
  background: #f5f5f5;
}
.modal-content .modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}
.modal-content .cancel-btn {
  padding: 8px 20px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: #fff;
  color: #666;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}
.modal-content .cancel-btn:hover {
  background: #f5f5f5;
}
.modal-content .confirm-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}
.modal-content .confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(255, 106, 0, 0.3);
}

/* ============ 道具商城 ============ */
.shop-page {
  padding: 4px 0;
}
.shop-cdkey-page {
  padding: 8px 0;
}
.shop-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
}
.shop-tab {
  padding: 10px 22px;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: color 0.2s;
}
.shop-tab:hover {
  color: #ff6a00;
}
.shop-tab.active {
  color: #ff6a00;
  font-weight: 600;
}
.shop-tab.active::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -1px;
  transform: translateX(-50%);
  width: 28px;
  height: 3px;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border-radius: 2px;
}
.shop-category-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}
.shop-category-item {
  padding: 6px 16px;
  font-size: 13px;
  color: #555;
  background: #f5f5f5;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s;
}
.shop-category-item:hover {
  color: #ff6a00;
  background: #fff3e6;
}
.shop-category-item.active {
  color: #fff;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
}
.shop-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}
.shop-card {
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.25s;
  display: flex;
  flex-direction: column;
}
.shop-card:hover {
  border-color: #ff9600;
  box-shadow: 0 6px 18px rgba(255, 106, 0, 0.12);
  transform: translateY(-2px);
}
.shop-card-icon {
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fff7ed 0%, #fffaf3 100%);
  border-bottom: 1px solid #f5f0eb;
}
.shop-card-icon img {
  max-width: 80%;
  max-height: 80%;
  object-fit: contain;
}
.shop-card-info {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}
.shop-card-name {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.shop-card-desc {
  font-size: 12px;
  color: #999;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.shop-card-bottom {
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.shop-card-price {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 16px;
  font-weight: 700;
  color: #ff6a00;
}
.shop-buy-btn,
.shop-use-btn {
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(90deg, #ff6a00, #ff9600);
  border: none;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.2s;
}
.shop-buy-btn:hover,
.shop-use-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(255, 106, 0, 0.3);
}
.shop-empty {
  text-align: center;
  padding: 48px 20px;
  color: #999;
}
.shop-empty img {
  width: 80px;
  height: 80px;
  object-fit: contain;
  opacity: 0.5;
  margin-bottom: 12px;
}
.shop-empty p {
  margin: 0 0 16px;
  font-size: 14px;
}

@media (max-width: 1100px) {
  .home-dashboard {
    align-items: flex-start;
    flex-direction: column;
    gap: 20px;
  }
  .home-actions {
    width: 100%;
  }
  .home-action {
    flex: 1;
    width: auto;
  }
}

@media (max-width: 700px) {
  .home-actions,
  .assets-overview {
    display: grid;
    grid-template-columns: 1fr;
  }
  .home-action {
    width: 100%;
  }
  .asset-score-panel {
    grid-template-columns: 44px 1fr auto;
  }
  .asset-balance-head {
    align-items: flex-start;
    flex-direction: column;
  }
  .asset-guides > div {
    grid-template-columns: 76px 1fr;
    padding: 10px 0;
  }
  .asset-guides button {
    grid-column: 2;
    justify-self: start;
  }
  .commission-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
